import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Car, Settings, LogOut, Plus, Pencil, Trash2, X, Loader2,
  Phone, Mail, MapPin, Share2, Star, Inbox, KeyRound, Upload, Bell, Eye, EyeOff, Images, Film, PlusCircle,
  Ship, HandCoins, FileCheck, Users, ShieldCheck, Activity, Download, CheckCircle2, Ban, RotateCcw, Calculator, MessageSquareQuote, FileText, ImagePlus, UserCheck, Lock, Megaphone, BarChart3,
} from "lucide-react";

const IMPORT_STATUSES = ["Pedido recebido","Em análise","Pesquisa de viaturas","Viatura encontrada","Proposta enviada","Aguardar aprovação do cliente","Viatura reservada","Em transporte","Em legalização","Pronto para entrega","Processo concluído"];
const FIN_STATUSES = ["Pedido recebido","Documentação em análise","Documentação incompleta","A aguardar documentos do cliente","Em análise pela Solution Car Premium","Enviado à instituição financeira","Em análise pela instituição financeira","Financiamento aprovado","Financiamento recusado","A aguardar assinatura do contrato","Financiamento concluído"];
const DOC_REVIEW = ["aprovado","rejeitado","reenviar"];

import { getUser, authHeaders as sharedAuthHeaders, clearAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "suv", label: "SUV" },
  { id: "sedan", label: "Sedan" },
  { id: "hatchback", label: "Hatchback" },
  { id: "coupe", label: "Coupé" },
  { id: "carrinha", label: "Carrinha" },
  { id: "citadino", label: "Citadino" },
];

const emptyVehicle = {
  title: "", category: "suv", year: new Date().getFullYear(), km: 0,
  fuel: "Gasolina", transmission: "Manual", price: 0, image_url: "", badge: "", featured: false,
  brand: "", model: "", version: "", displacement: "", power: "", color: "", doors: "",
  description: "", equipmentText: "", condition: "Usado", location: "", images: [], video_url: "", published: true,
};

function authHeaders() {
  return sharedAuthHeaders();
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [leads, setLeads] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [consents, setConsents] = useState([]);
  const [privacyRequests, setPrivacyRequests] = useState([]);
  const [ads, setAds] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // vehicle form state
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // vehicle id or null
  const [form, setForm] = useState(emptyVehicle);
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // {title, message, onConfirm}
  const [uploading, setUploading] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [account, setAccount] = useState({ name: "", email: "" });
  const [acSaving, setAcSaving] = useState(false);
  // platform state
  const [processes, setProcesses] = useState([]);
  const [selProcess, setSelProcess] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: "", note: "" });
  const [procMsg, setProcMsg] = useState("");
  const [procInternal, setProcInternal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [me, setMe] = useState(null);
  const [newClient, setNewClient] = useState({ name: "", email: "", password: "", phone: "" });
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "", role: "funcionario" });

  const loadAll = async () => {
    try {
      const h = authHeaders();
      const [l, v, s, p, d, c, a, meRes, t, se, dl, co, pr, ad] = await Promise.all([
        axios.get(`${API}/leads`, { headers: h }),
        axios.get(`${API}/admin/vehicles`, { headers: h }),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/processes`, { headers: h }),
        axios.get(`${API}/admin/documents`, { headers: h }),
        axios.get(`${API}/admin/clients`, { headers: h }),
        axios.get(`${API}/admin/audit`, { headers: h }),
        axios.get(`${API}/auth/me`, { headers: h }),
        axios.get(`${API}/admin/testimonials`, { headers: h }),
        axios.get(`${API}/admin/sellers`, { headers: h }),
        axios.get(`${API}/admin/deals`, { headers: h }),
        axios.get(`${API}/admin/privacy/consents`, { headers: h }),
        axios.get(`${API}/admin/privacy/requests`, { headers: h }),
        axios.get(`${API}/admin/ads`, { headers: h }),
      ]);
      setLeads(l.data);
      setVehicles(v.data);
      setSettings(s.data);
      setProcesses(p.data);
      setDocuments(d.data);
      setClients(c.data);
      setAuditLog(a.data);
      setMe(meRes.data);
      setTestimonials(t.data);
      setSellers(se.data);
      setDeals(dl.data);
      setConsents(co.data);
      setPrivacyRequests(pr.data);
      setAds(ad.data);
      setAccount({ name: meRes.data.name || "", email: meRes.data.email || "" });
      if (meRes.data.role === "superadmin") {
        const st = await axios.get(`${API}/admin/staff`, { headers: h });
        setStaff(st.data);
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        clearAuth();
        navigate("/entrar");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const u = getUser();
    if (!u || !["superadmin", "admin", "funcionario"].includes(u.role)) {
      navigate("/entrar");
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearAuth();
    navigate("/entrar");
  };

  // ---- vehicle CRUD ----
  const openCreate = () => {
    setEditing(null);
    setForm(emptyVehicle);
    setShowForm(true);
  };

  const openEdit = (v) => {
    setEditing(v.id);
    setForm({ ...v, badge: v.badge || "", equipmentText: (v.equipment || []).join("\n"), images: v.images || [] });
    setShowForm(true);
  };

  const setF = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const saveVehicle = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const images = (form.images || []).filter(Boolean);
      const payload = {
        ...form,
        year: Number(form.year), km: Number(form.km), price: Number(form.price),
        displacement: form.displacement ? Number(form.displacement) : null,
        power: form.power ? Number(form.power) : null,
        doors: form.doors ? Number(form.doors) : null,
        badge: form.badge || null,
        equipment: (form.equipmentText || "").split("\n").map((s) => s.trim()).filter(Boolean),
        images,
        // ensure a primary image is always set when photos exist
        image_url: form.image_url || images[0] || "",
      };
      delete payload.equipmentText;
      if (editing) {
        await axios.put(`${API}/admin/vehicles/${editing}`, payload, { headers: authHeaders() });
        toast.success("Viatura atualizada.");
      } else {
        await axios.post(`${API}/admin/vehicles`, payload, { headers: authHeaders() });
        toast.success("Viatura adicionada.");
      }
      setShowForm(false);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao guardar a viatura.");
    } finally {
      setSaving(false);
    }
  };

  const removeVehicle = (v) => {
    openConfirm(
      "Eliminar viatura",
      `Eliminar "${v.title}" definitivamente? A viatura é removida do painel e do website. Esta ação não pode ser anulada.`,
      async () => {
        try {
          await axios.delete(`${API}/admin/vehicles/${v.id}`, { headers: authHeaders() });
          toast.success("Viatura eliminada.");
          await loadAll();
        } catch {
          toast.error("Erro ao eliminar a viatura.");
        }
      }
    );
  };

  const togglePublish = async (v) => {
    try {
      const res = await axios.patch(`${API}/admin/vehicles/${v.id}/publish`, {}, { headers: authHeaders() });
      toast.success(res.data.published ? "Viatura publicada no website." : "Viatura ocultada do website.");
      await loadAll();
    } catch {
      toast.error("Erro ao alterar a publicação.");
    }
  };

  const toggleVehicleFlag = async (v, flag) => {
    try {
      const res = await axios.patch(`${API}/admin/vehicles/${v.id}/${flag}`, {}, { headers: authHeaders() });
      await loadAll();
    } catch {
      toast.error("Erro ao atualizar a viatura.");
    }
  };

  const setVehicleAvailable = async (v) => {
    try {
      if (v.sold) await axios.patch(`${API}/admin/vehicles/${v.id}/sold`, {}, { headers: authHeaders() });
      if (v.reserved) await axios.patch(`${API}/admin/vehicles/${v.id}/reserved`, {}, { headers: authHeaders() });
      toast.success("Viatura disponível novamente.");
      await loadAll();
    } catch {
      toast.error("Erro ao atualizar a viatura.");
    }
  };

  // ---- platform actions ----
  const openProcess = async (p) => {
    try {
      const { data } = await axios.get(`${API}/admin/processes/${p.id}`, { headers: authHeaders() });
      setSelProcess(data);
      setStatusUpdate({ status: data.status, note: "" });
    } catch { toast.error("Erro ao abrir o processo."); }
  };

  const updateProcessStatus = async () => {
    if (!statusUpdate.status) return;
    try {
      await axios.patch(`${API}/admin/processes/${selProcess.id}/status`, statusUpdate, { headers: authHeaders() });
      toast.success("Estado atualizado e cliente notificado.");
      await openProcess(selProcess);
      await loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao atualizar o estado."); }
  };

  const sendProcMessage = async (e) => {
    e.preventDefault();
    if (!procMsg.trim()) return;
    try {
      await axios.post(`${API}/admin/processes/${selProcess.id}/messages?internal=${procInternal}`, { body: procMsg }, { headers: authHeaders() });
      setProcMsg("");
      toast.success(procInternal ? "Nota interna adicionada." : "Mensagem enviada ao cliente.");
      await openProcess(selProcess);
    } catch { toast.error("Erro ao enviar."); }
  };

  const reviewDoc = (doc, status) => {
    const run = async (note = "") => {
      try {
        await axios.patch(`${API}/admin/documents/${doc.id}/review`, { status, note }, { headers: authHeaders() });
        toast.success(`Documento ${status === "aprovado" ? "aprovado" : status === "rejeitado" ? "rejeitado" : "marcado para reenvio"}.`);
        await loadAll();
      } catch { toast.error("Erro ao rever o documento."); }
    };
    run();
  };

  const createClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/clients`, newClient, { headers: authHeaders() });
      toast.success("Cliente criado.");
      setNewClient({ name: "", email: "", password: "", phone: "" });
      await loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao criar cliente."); }
  };

  const toggleClient = async (c, action) => {
    try {
      const res = await axios.patch(`${API}/admin/clients/${c._id}/${action}`, {}, { headers: authHeaders() });
      toast.success(action === "block" ? (res.data.blocked ? "Conta bloqueada." : "Conta desbloqueada.") : (res.data.disabled ? "Conta desativada." : "Conta reativada."));
      await loadAll();
    } catch { toast.error("Erro."); }
  };

  const acceptClient = async (c) => {
    try {
      await axios.patch(`${API}/admin/clients/${c._id}/accept`, {}, { headers: authHeaders() });
      toast.success("Cliente aceite.");
      await loadAll();
    } catch { toast.error("Erro ao aceitar o cliente."); }
  };

  const deleteClient = (c) => {
    openConfirm(
      "Eliminar cliente",
      `Eliminar o cliente "${c.name}"? A conta fica inativa (os processos e documentos mantêm-se).`,
      async () => {
        try {
          await axios.delete(`${API}/admin/clients/${c._id}`, { headers: authHeaders() });
          toast.success("Cliente eliminado.");
          await loadAll();
        } catch { toast.error("Erro ao eliminar o cliente."); }
      }
    );
  };

  const processAction = (p, action) => {
    const run = async () => {
      try {
        if (action === "delete") {
          await axios.delete(`${API}/admin/processes/${p.id}`, { headers: authHeaders() });
        } else {
          await axios.patch(`${API}/admin/processes/${p.id}/${action}`, {}, { headers: authHeaders() });
        }
        toast.success(`Pedido ${action === "accept" ? "aceite" : action === "reject" ? "recusado" : "eliminado"}.`);
        setSelProcess(null);
        await loadAll();
      } catch { toast.error("Erro."); }
    };
    if (action === "delete") {
      openConfirm("Eliminar pedido", `Eliminar este pedido de ${p.client_name}?`, run);
    } else {
      run();
    }
  };

  const leadAction = (l, action) => {
    const run = async () => {
      try {
        if (action === "delete") {
          await axios.delete(`${API}/admin/leads/${l.id}`, { headers: authHeaders() });
        } else {
          await axios.patch(`${API}/admin/leads/${l.id}/${action}`, {}, { headers: authHeaders() });
        }
        toast.success(`Pedido ${action === "accept" ? "aceite" : action === "reject" ? "recusado" : "eliminado"}.`);
        await loadAll();
      } catch { toast.error("Erro."); }
    };
    if (action === "delete") {
      openConfirm("Eliminar pedido", `Eliminar o pedido de "${l.name}"?`, run);
    } else {
      run();
    }
  };

  const testimonialAction = (t, action) => {
    const run = async () => {
      try {
        if (action === "delete") {
          await axios.delete(`${API}/admin/testimonials/${t.id}`, { headers: authHeaders() });
        } else {
          await axios.patch(`${API}/admin/testimonials/${t.id}/${action}`, {}, { headers: authHeaders() });
        }
        toast.success(`Testemunho ${action === "approve" ? "aprovado" : action === "reject" ? "rejeitado" : "eliminado"}.`);
        await loadAll();
      } catch { toast.error("Erro."); }
    };
    if (action === "delete") {
      openConfirm("Eliminar testemunho", `Eliminar permanentemente o testemunho de "${t.name}"?`, run);
    } else {
      run();
    }
  };

  const privacyRequestAction = async (r, status) => {
    try {
      await axios.patch(`${API}/admin/privacy/requests/${r.id}`, { status }, { headers: authHeaders() });
      toast.success("Pedido atualizado.");
      await loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro."); }
  };

  // ---- Gestão de Publicidade ----
  const emptyAd = { name: "", content_type: "image", media_url: "", title: "", description: "", link_url: "", button_text: "", start_date: "", end_date: "", active: true, order: 0, duration_seconds: 6, open_new_tab: false, autoplay_video: false };
  const [adForm, setAdForm] = useState(emptyAd);
  const [editingAdId, setEditingAdId] = useState(null);
  const [adSaving, setAdSaving] = useState(false);
  const [adUploading, setAdUploading] = useState(false);

  const resetAdForm = () => { setAdForm(emptyAd); setEditingAdId(null); };

  const handleAdUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setAdUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, data, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      const isVideo = (file.type || "").startsWith("video/");
      setAdForm((f) => ({ ...f, media_url: res.data.url, content_type: isVideo ? "video" : "image" }));
      toast.success(isVideo ? "Vídeo carregado." : "Imagem carregada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar o ficheiro.");
    } finally {
      setAdUploading(false);
      e.target.value = "";
    }
  };

  const saveAd = async (e) => {
    e.preventDefault();
    if (adSaving) return;
    setAdSaving(true);
    try {
      const payload = { ...adForm, start_date: adForm.start_date || null, end_date: adForm.end_date || null };
      if (editingAdId) {
        await axios.put(`${API}/admin/ads/${editingAdId}`, payload, { headers: authHeaders() });
        toast.success("Anúncio atualizado.");
      } else {
        await axios.post(`${API}/admin/ads`, payload, { headers: authHeaders() });
        toast.success("Anúncio criado.");
      }
      resetAdForm();
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao guardar o anúncio.");
    } finally {
      setAdSaving(false);
    }
  };

  const editAd = (a) => {
    setAdForm({ ...emptyAd, ...a, start_date: a.start_date ? a.start_date.slice(0, 16) : "", end_date: a.end_date ? a.end_date.slice(0, 16) : "" });
    setEditingAdId(a.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleAd = async (a) => {
    try {
      await axios.put(`${API}/admin/ads/${a.id}`, { active: !a.active }, { headers: authHeaders() });
      await loadAll();
    } catch { toast.error("Erro."); }
  };

  const deleteAd = (a) => {
    openConfirm("Eliminar anúncio", `Eliminar o anúncio "${a.name}"? Esta ação é permanente.`, async () => {
      try {
        await axios.delete(`${API}/admin/ads/${a.id}`, { headers: authHeaders() });
        toast.success("Anúncio eliminado.");
        if (editingAdId === a.id) resetAdForm();
        await loadAll();
      } catch { toast.error("Erro ao eliminar."); }
    });
  };

  const toggleCommission = async (d) => {
    try {
      await axios.patch(`${API}/admin/deals/${d.id}/commission`, {}, { headers: authHeaders() });
      toast.success(d.commission_paid ? "Comissão marcada como por pagar." : "Comissão marcada como paga.");
      await loadAll();
    } catch { toast.error("Erro."); }
  };

  const sellerAction = (s, action) => {
    const run = async () => {
      try {
        if (action === "delete") {
          await axios.delete(`${API}/admin/sellers/${s.id}`, { headers: authHeaders() });
        } else {
          await axios.patch(`${API}/admin/sellers/${s.id}/${action}`, {}, { headers: authHeaders() });
        }
        toast.success(`Vendedor ${action === "accept" ? "aprovado" : action === "reject" ? "recusado" : action === "delete" ? "eliminado" : s.blocked ? "desbloqueado" : "bloqueado"}.`);
        await loadAll();
      } catch { toast.error("Erro."); }
    };
    if (action === "delete") {
      openConfirm("Eliminar vendedor", `Eliminar a conta de "${s.name}"? O histórico de vendas e registos é preservado.`, run);
    } else if (action === "reject") {
      openConfirm("Recusar candidatura", `Recusar a candidatura de "${s.name}"?`, run);
    } else {
      run();
    }
  };

  const deleteDocument = (d) => {
    openConfirm(
      "Eliminar documento",
      `Eliminar o documento "${d.original_filename}"? Os restantes documentos mantêm-se.`,
      async () => {
        try {
          await axios.delete(`${API}/admin/documents/${d.id}`, { headers: authHeaders() });
          toast.success("Documento eliminado.");
          await loadAll();
        } catch { toast.error("Erro ao eliminar o documento."); }
      }
    );
  };

  const acceptStaff = async (s) => {
    try {
      await axios.patch(`${API}/admin/staff/${s._id}/accept`, {}, { headers: authHeaders() });
      toast.success("Membro aceite.");
      await loadAll();
    } catch { toast.error("Erro ao aceitar."); }
  };

  const deleteStaff = (s) => {
    openConfirm(
      "Eliminar membro",
      `Eliminar o membro "${s.name}"? Fica sem acesso.`,
      async () => {
        try {
          await axios.delete(`${API}/admin/staff/${s._id}`, { headers: authHeaders() });
          toast.success("Membro eliminado.");
          await loadAll();
        } catch { toast.error("Erro ao eliminar."); }
      }
    );
  };

  // auditoria
  const [selectedAudit, setSelectedAudit] = useState([]);

  const openConfirm = (title, message, onConfirm) => setConfirmDialog({ title, message, onConfirm });
  const closeConfirm = () => setConfirmDialog(null);
  const toggleAuditSelect = (id) => setSelectedAudit((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const deleteAuditOne = (id) => {
    openConfirm("Eliminar registo", "Eliminar este registo de atividade?", async () => {
      try {
        await axios.delete(`${API}/admin/audit/${id}`, { headers: authHeaders() });
        toast.success("Registo eliminado.");
        setSelectedAudit((s) => s.filter((x) => x !== id));
        await loadAll();
      } catch { toast.error("Erro."); }
    });
  };
  const deleteAuditMany = () => {
    if (selectedAudit.length === 0) return;
    openConfirm("Eliminar selecionados", `Eliminar ${selectedAudit.length} registo(s) selecionado(s)?`, async () => {
      try {
        await axios.post(`${API}/admin/audit/delete-many`, { ids: selectedAudit }, { headers: authHeaders() });
        toast.success(`${selectedAudit.length} registo(s) eliminado(s).`);
        setSelectedAudit([]);
        await loadAll();
      } catch { toast.error("Erro."); }
    });
  };
  const deleteAuditAll = () => {
    openConfirm("Eliminar tudo", "ATENÇÃO: eliminar TODOS os registos de atividade? Esta ação não pode ser anulada.", async () => {
      try {
        await axios.delete(`${API}/admin/audit`, { headers: authHeaders() });
        toast.success("Todos os registos eliminados.");
        setSelectedAudit([]);
        await loadAll();
      } catch { toast.error("Erro."); }
    });
  };

  const createStaff = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/staff`, newStaff, { headers: authHeaders() });
      toast.success("Utilizador criado.");
      setNewStaff({ name: "", email: "", password: "", role: "funcionario" });
      await loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao criar utilizador."); }
  };

  const toggleStaff = async (s) => {
    try {
      const res = await axios.patch(`${API}/admin/staff/${s._id}/block`, {}, { headers: authHeaders() });
      toast.success(res.data.blocked ? "Utilizador bloqueado." : "Utilizador reativado.");
      await loadAll();
    } catch { toast.error("Erro."); }
  };

  const saveAccount = async (e) => {
    e.preventDefault();
    if (acSaving) return;
    setAcSaving(true);
    try {
      const res = await axios.put(`${API}/account`, account, { headers: authHeaders() });
      // Atualiza o utilizador em sessão
      const u = getUser() || {};
      const updated = { ...u, name: res.data.name, email: res.data.email };
      localStorage.setItem("user", JSON.stringify(updated));
      toast.success("Dados de acesso atualizados.");
      if (res.data.email !== u.email) {
        toast.info("Use o novo email no próximo início de sessão.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar.");
    } finally {
      setAcSaving(false);
    }
  };

  // ---- settings ----
  const [settingsSaving, setSettingsSaving] = useState(false);

  // ---- Documentos legais ----
  const LEGAL_DOCS = [
    { key: "quem_somos", label: "Quem Somos", path: "/quem-somos" },
    { key: "privacidade", label: "Política de Privacidade", path: "/politica-de-privacidade" },
    { key: "protecao_dados", label: "Política de Proteção de Dados", path: "/protecao-de-dados" },
    { key: "cookies", label: "Política de Cookies", path: "/politica-de-cookies" },
    { key: "termos", label: "Termos e Condições de Utilização", path: "/termos-e-condicoes" },
  ];
  const [legalDocKey, setLegalDocKey] = useState("quem_somos");
  const [legalSaving, setLegalSaving] = useState(false);
  const [legalPreview, setLegalPreview] = useState(false);

  const currentLegalDoc = (settings?.legal_docs || {})[legalDocKey] || { title: LEGAL_DOCS.find((d) => d.key === legalDocKey)?.label || "", content: "", status: "draft", updated_at: "" };

  const setLegalDoc = (patch) => {
    setSettings((s) => ({
      ...s,
      legal_docs: { ...(s.legal_docs || {}), [legalDocKey]: { ...currentLegalDoc, ...patch } },
    }));
  };

  const saveLegalDoc = async (publish) => {
    if (legalSaving) return;
    setLegalSaving(true);
    try {
      const doc = { ...currentLegalDoc, status: publish ? "published" : currentLegalDoc.status, updated_at: new Date().toISOString() };
      const legal_docs = { ...(settings.legal_docs || {}), [legalDocKey]: doc };
      const { data } = await axios.put(`${API}/admin/settings`, { ...settings, legal_docs }, { headers: authHeaders() });
      setSettings(data);
      toast.success(publish ? "Documento publicado no website." : "Documento guardado.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao guardar o documento.");
    } finally {
      setLegalSaving(false);
    }
  };

  const saveSettings = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (settingsSaving) return;
    setSettingsSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, settings, { headers: authHeaders() });
      setSettings(data);
      toast.success("Definições guardadas. O website já reflete as alterações.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao guardar as definições. Tente novamente.");
    } finally {
      setSettingsSaving(false);
    }
  };

  const setS = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.value }));

  // ---- Gestão de Páginas: galeria de clientes ----
  const [galleryForm, setGalleryForm] = useState({ media_type: "image", media_url: "", name: "", description: "", published: true });
  const [editingGalleryId, setEditingGalleryId] = useState(null);
  const [galleryUploading, setGalleryUploading] = useState(false);

  const galleryItems = settings?.gallery_items || [];

  const resetGalleryForm = () => {
    setGalleryForm({ media_type: "image", media_url: "", name: "", description: "", published: true });
    setEditingGalleryId(null);
  };

  const handleGalleryUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setGalleryUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, data, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      const isVideo = (file.type || "").startsWith("video/");
      setGalleryForm((f) => ({ ...f, media_url: res.data.url, media_type: isVideo ? "video" : "image" }));
      toast.success(isVideo ? "Vídeo carregado." : "Fotografia carregada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar o ficheiro.");
    } finally {
      setGalleryUploading(false);
      e.target.value = "";
    }
  };

  const saveGalleryItem = (e) => {
    e.preventDefault();
    if (!galleryForm.media_url) {
      toast.error("Carregue uma fotografia ou vídeo primeiro.");
      return;
    }
    setSettings((s) => {
      const items = [...(s.gallery_items || [])];
      if (editingGalleryId) {
        const idx = items.findIndex((g) => g.id === editingGalleryId);
        if (idx >= 0) items[idx] = { ...items[idx], ...galleryForm };
      } else {
        items.push({ ...galleryForm, id: `g-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` });
      }
      return { ...s, gallery_items: items };
    });
    toast.success(editingGalleryId ? "Publicação atualizada. Guarde as alterações." : "Publicação adicionada. Guarde as alterações.");
    resetGalleryForm();
  };

  const editGalleryItem = (g) => {
    setGalleryForm({ media_type: g.media_type, media_url: g.media_url, name: g.name, description: g.description, published: g.published });
    setEditingGalleryId(g.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteGalleryItem = (g) => {
    openConfirm("Eliminar publicação", `Eliminar a publicação de "${g.name || "cliente"}"?`, () => {
      setSettings((s) => ({ ...s, gallery_items: (s.gallery_items || []).filter((x) => x.id !== g.id) }));
      if (editingGalleryId === g.id) resetGalleryForm();
      toast.success("Publicação removida. Guarde as alterações.");
    });
  };

  const toggleGalleryPublished = (g) => {
    setSettings((s) => ({
      ...s,
      gallery_items: (s.gallery_items || []).map((x) => (x.id === g.id ? { ...x, published: !x.published } : x)),
    }));
  };

  const addAboutImage = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setGalleryUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, data, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setSettings((s) => ({ ...s, about_images: [...(s.about_images || []), res.data.url] }));
      toast.success("Imagem adicionada. Guarde as alterações.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar a imagem.");
    } finally {
      setGalleryUploading(false);
      e.target.value = "";
    }
  };

  const removeAboutImage = (idx) => {
    setSettings((s) => ({ ...s, about_images: (s.about_images || []).filter((_, i) => i !== idx) }));
  };

  // ---- image upload (adds to images gallery + sets cover if empty) ----
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const data = new FormData();
        data.append("file", file);
        const res = await axios.post(`${API}/admin/upload`, data, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
        const url = res.data.url;
        setForm((f) => ({
          ...f,
          images: [...(f.images || []), url],
          image_url: f.image_url || url,
        }));
      }
      toast.success(files.length > 1 ? `${files.length} fotografias adicionadas.` : "Fotografia adicionada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar a fotografia.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => {
      const images = (f.images || []).filter((_, i) => i !== idx);
      return { ...f, images, image_url: images[0] || "" };
    });
  };

  // ---- change password ----
  const changePassword = async (e) => {
    e.preventDefault();
    if (pwSaving) return;
    if (pw.new_password.length < 8) {
      toast.error("A nova palavra-passe deve ter pelo menos 8 caracteres.");
      return;
    }
    if (pw.new_password !== pw.confirm) {
      toast.error("A confirmação não coincide com a nova palavra-passe.");
      return;
    }
    setPwSaving(true);
    try {
      await axios.put(
        `${API}/admin/password`,
        { current_password: pw.current_password, new_password: pw.new_password },
        { headers: authHeaders() }
      );
      toast.success("Palavra-passe alterada com sucesso.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar a palavra-passe.");
    } finally {
      setPwSaving(false);
    }
  };

  const TABS = [
    { id: "overview", label: "Resumo", icon: LayoutDashboard },
    { id: "vehicles", label: "Viaturas", icon: Car },
    { id: "processes", label: "Processos", icon: FileCheck },
    { id: "documents", label: "Documentos", icon: Upload },
    { id: "clients", label: "Clientes", icon: Users },
    ...(me?.role === "superadmin" ? [{ id: "staff", label: "Equipa", icon: ShieldCheck }] : []),
    { id: "leads", label: "Pedidos Site", icon: Inbox },
    { id: "testimonials", label: "Testemunhos", icon: MessageSquareQuote },
    { id: "sellers", label: "Vendedores", icon: UserCheck },
    { id: "sales", label: "Vendas", icon: BarChart3 },
    { id: "pages", label: "Gestão de Páginas", icon: FileText },
    { id: "ads", label: "Publicidade", icon: Megaphone },
    { id: "privacy", label: "Privacidade", icon: Lock },
    { id: "audit", label: "Atividade", icon: Activity },
    { id: "settings", label: "Definições", icon: Settings },
    { id: "password", label: "Conta", icon: KeyRound },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="admin-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const recentLeads = leads.slice(0, 5);
  const fmtEur = (n) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-[#0B1E36] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 min-h-16 py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-3">
            <span className="font-display font-bold tracking-tight text-sm sm:text-base">SOLUTION CAR PREMIUM</span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400 border border-white/20 px-2 py-0.5">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-xs text-slate-300 hover:text-white whitespace-nowrap" data-testid="admin-view-site">
              Ver website
            </a>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white whitespace-nowrap">
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium border transition-colors ${
                tab === id
                  ? "bg-[#0072C6] text-white border-[#0072C6]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-[#0072C6] hover:text-[#0072C6]"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
              {id === "leads" && leads.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 ${tab === id ? "bg-white text-[#0072C6]" : "bg-[#0072C6] text-white"}`}>
                  {leads.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <div data-testid="admin-overview">
            <div className="grid sm:grid-cols-3 gap-5 mb-10">
              <div className="bg-white border border-slate-200 p-6" data-testid="stat-vehicles">
                <p className="text-xs uppercase tracking-wider text-slate-500">Viaturas no stock</p>
                <p className="mt-2 font-display text-4xl text-[#0072C6]">{vehicles.length}</p>
              </div>
              <div className="bg-white border border-slate-200 p-6" data-testid="stat-featured">
                <p className="text-xs uppercase tracking-wider text-slate-500">Em destaque</p>
                <p className="mt-2 font-display text-4xl text-[#0072C6]">{vehicles.filter((v) => v.featured).length}</p>
              </div>
              <div className="bg-white border border-slate-200 p-6" data-testid="stat-leads">
                <p className="text-xs uppercase tracking-wider text-slate-500">Pedidos recebidos</p>
                <p className="mt-2 font-display text-4xl text-[#0072C6]">{leads.length}</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-display text-xl text-[#0072C6]">Últimos pedidos</h2>
                  <button onClick={() => setTab("leads")} className="text-xs font-semibold text-[#0072C6] underline">
                    Ver todos
                  </button>
                </div>
                {recentLeads.length === 0 ? (
                  <p className="text-sm text-slate-400">Ainda não há pedidos.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {recentLeads.map((l) => (
                      <li key={l.id} className="py-3 flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-[#0072C6]">{l.name}</p>
                          <p className="text-xs text-slate-500">{l.inquiry_type} · {l.phone}</p>
                        </div>
                        <span className="text-[11px] text-slate-400 shrink-0">
                          {new Date(l.created_at).toLocaleDateString("pt-PT")}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-display text-xl text-[#0072C6] mb-5">Ações rápidas</h2>
                <div className="space-y-3">
                  <button onClick={() => { setTab("vehicles"); openCreate(); }} data-testid="quick-add-vehicle"
                    className="w-full inline-flex items-center gap-3 border border-slate-200 px-5 py-4 text-sm font-medium text-slate-700 hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                    <Plus className="w-4 h-4" /> Adicionar nova viatura
                  </button>
                  <button onClick={() => setTab("settings")} data-testid="quick-edit-contacts"
                    className="w-full inline-flex items-center gap-3 border border-slate-200 px-5 py-4 text-sm font-medium text-slate-700 hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                    <Phone className="w-4 h-4" /> Atualizar contactos e redes sociais
                  </button>
                  <button onClick={() => setTab("leads")} data-testid="quick-view-leads"
                    className="w-full inline-flex items-center gap-3 border border-slate-200 px-5 py-4 text-sm font-medium text-slate-700 hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                    <Inbox className="w-4 h-4" /> Consultar pedidos de clientes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VEHICLES */}
        {tab === "vehicles" && (
          <div data-testid="admin-vehicles">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-[#0072C6]">Gestão de Viaturas</h2>
              <button onClick={openCreate} data-testid="add-vehicle-button"
                className="inline-flex items-center gap-2 bg-[#0072C6] text-white text-sm font-semibold px-5 py-3 hover:bg-[#005FA3] transition-colors">
                <Plus className="w-4 h-4" /> Adicionar Viatura
              </button>
            </div>

            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm" data-testid="vehicles-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3.5">Viatura</th>
                    <th className="px-5 py-3.5">Marca/Modelo</th>
                    <th className="px-5 py-3.5">Ano</th>
                    <th className="px-5 py-3.5">Preço</th>
                    <th className="px-5 py-3.5">Destaque</th>
                    <th className="px-5 py-3.5">Publicada</th>
                    <th className="px-5 py-3.5">Estado</th>
                    <th className="px-5 py-3.5 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {vehicles.map((v) => (
                    <tr key={v.id} data-testid={`vehicle-row-${v.id}`}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <img src={(v.images && v.images[0]) || v.image_url} alt="" className="w-14 h-10 object-cover border border-slate-200" />
                          <span className="font-medium text-[#0072C6]">{v.title}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-slate-600">{[v.brand, v.model].filter(Boolean).join(" ") || "—"}</td>
                      <td className="px-5 py-3.5 text-slate-600">{v.year}</td>
                      <td className="px-5 py-3.5 font-medium text-[#0072C6]">{v.price.toLocaleString("pt-PT")} €</td>
                      <td className="px-5 py-3.5">
                        {v.featured ? <Star className="w-4 h-4 text-[#0072C6] fill-[#0072C6]" /> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-5 py-3.5">
                        <button onClick={() => togglePublish(v)} data-testid={`publish-vehicle-${v.id}`}
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 transition-colors ${
                            v.published !== false
                              ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                          title={v.published !== false ? "Ocultar do website" : "Publicar no website"}>
                          {v.published !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          {v.published !== false ? "Publicada" : "Oculta"}
                        </button>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col gap-1.5">
                          {/* Vendido / Reservado / Disponível */}
                          <div className="flex gap-1">
                            <button onClick={() => toggleVehicleFlag(v, "sold")} data-testid={`sold-vehicle-${v.id}`}
                              title="Marcar como vendido"
                              className={`text-[10px] font-semibold px-2 py-1 transition-colors ${v.sold ? "bg-red-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-700"}`}>
                              Vendido
                            </button>
                            <button onClick={() => toggleVehicleFlag(v, "reserved")} data-testid={`reserved-vehicle-${v.id}`}
                              title="Marcar como reservado"
                              className={`text-[10px] font-semibold px-2 py-1 transition-colors ${v.reserved ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-amber-50 hover:text-amber-700"}`}>
                              Reservado
                            </button>
                            {(v.sold || v.reserved) && (
                              <button onClick={() => setVehicleAvailable(v)} data-testid={`available-vehicle-${v.id}`}
                                title="Voltar a disponível"
                                className="text-[10px] font-semibold px-2 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">
                                Disponível
                              </button>
                            )}
                          </div>
                          {/* Bloquear */}
                          <button onClick={() => toggleVehicleFlag(v, "block")} data-testid={`block-vehicle-${v.id}`}
                            className={`text-[10px] font-semibold px-2 py-1 transition-colors self-start ${v.blocked ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-700"}`}>
                            {v.blocked ? "Bloqueado" : "Bloquear"}
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`}
                          className="p-2 text-slate-500 hover:text-[#0072C6]" aria-label="Editar">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => removeVehicle(v)} data-testid={`delete-vehicle-${v.id}`}
                          className="p-2 text-slate-500 hover:text-red-600" aria-label="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* LEADS */}
        {/* PROCESSES */}
        {tab === "processes" && (
          <div data-testid="admin-processes">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Importações e Financiamentos</h2>
            <div className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5 space-y-3">
                {processes.length === 0 ? (
                  <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">Ainda não há processos.</div>
                ) : processes.map((p) => (
                  <button key={p.id} onClick={() => openProcess(p)} data-testid={`admin-process-${p.id}`}
                    className={`w-full text-left bg-white border p-4 transition-colors ${selProcess?.id === p.id ? "border-[#0072C6]" : "border-slate-200 hover:border-[#0072C6]/40"}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {p.type === "importacao" ? <Ship className="w-5 h-5 text-[#0072C6]" /> : <HandCoins className="w-5 h-5 text-[#0072C6]" />}
                        <div>
                          <p className="font-semibold text-sm text-[#0072C6]">{p.client_name || p.first_holder?.name} <span className="font-normal text-slate-400 capitalize">· {p.type}</span></p>
                          <p className="text-xs text-slate-500">{p.type === "financiamento" && p.vehicle ? `${p.vehicle.brand || ""} ${p.vehicle.model || ""} — ${p.vehicle.price}€` : p.data?.brand ? `${p.data.brand} ${p.data.model || ""}` : p.data?.amount ? `${p.data.amount}€ / ${p.data.months}m` : ""}</p>
                        </div>
                      </div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#0072C6] text-white px-2 py-1 shrink-0">{p.status}</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="lg:col-span-7">
                {!selProcess ? (
                  <div className="bg-white border border-dashed border-slate-200 p-12 text-center text-slate-400 text-sm">Selecione um processo.</div>
                ) : (
                  <div className="bg-white border border-slate-200 p-6" data-testid="admin-process-detail">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-display text-xl text-[#0072C6] capitalize">{selProcess.type} — {selProcess.client_name}</h3>
                      <span className="text-xs text-slate-400">{selProcess.client_email}</span>
                    </div>

                    {/* Accept / Reject / Delete */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button onClick={() => processAction(selProcess, "accept")} data-testid="process-accept"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aceitar
                      </button>
                      <button onClick={() => processAction(selProcess, "reject")} data-testid="process-reject"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors">
                        <Ban className="w-3.5 h-3.5" /> Recusar
                      </button>
                      <button onClick={() => processAction(selProcess, "delete")} data-testid="process-delete"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" /> Eliminar
                      </button>
                    </div>

                    {/* Status update */}
                    <div className="mt-5 bg-[#F8FAFC] border border-slate-200 p-4">
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Atualizar estado</label>
                      <div className="flex flex-wrap gap-2">
                        <select data-testid="status-select" value={statusUpdate.status} onChange={(e) => setStatusUpdate((s) => ({ ...s, status: e.target.value }))}
                          className="border border-slate-200 px-3 py-2.5 text-sm bg-white flex-1 min-w-[200px]">
                          {(selProcess.type === "importacao" ? IMPORT_STATUSES : FIN_STATUSES).map((s) => <option key={s}>{s}</option>)}
                        </select>
                        <input data-testid="status-note" value={statusUpdate.note} onChange={(e) => setStatusUpdate((s) => ({ ...s, note: e.target.value }))}
                          placeholder="Nota (opcional)" className="border border-slate-200 px-3 py-2.5 text-sm flex-1 min-w-[160px]" />
                        <button onClick={updateProcessStatus} data-testid="status-save"
                          className="bg-[#0072C6] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#005FA3]">Guardar</button>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 text-xs text-slate-600">
                      {Object.entries(selProcess.data || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => (
                        <div key={k}><span className="text-slate-400 uppercase tracking-wider text-[10px]">{k}</span><br />{String(v)}</div>
                      ))}
                    </div>

                    {/* Financing structured detail */}
                    {selProcess.type === "financiamento" && selProcess.vehicle && (
                      <div className="mt-5 space-y-4" data-testid="admin-financing-detail">
                        {/* Vehicle */}
                        <div className="bg-[#F8FAFC] border border-slate-200 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wider text-[#0072C6] mb-2">Viatura</p>
                          <p className="text-sm text-slate-700">{selProcess.vehicle.brand} {selProcess.vehicle.model} — {selProcess.vehicle.title}</p>
                          <p className="text-xs text-slate-500">Preço {selProcess.vehicle.price}€ · Ano {selProcess.vehicle.year}{selProcess.vehicle.plate ? ` · ${selProcess.vehicle.plate}` : ""}</p>
                        </div>
                        {/* Financing conditions */}
                        <div className="bg-[#F8FAFC] border border-slate-200 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wider text-[#0072C6] mb-2">Condições</p>
                          <p className="text-sm text-slate-700">{selProcess.financing?.modality === "total" ? "Financiamento a 100% (sem entrada)" : `Com entrada de ${selProcess.financing?.down_payment}€`}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Retoma: {selProcess.trade_in?.has ? `${selProcess.trade_in.brand} ${selProcess.trade_in.model} (${selProcess.trade_in.paid_off === "sim" ? "quitada" : `falta ${selProcess.trade_in.remaining}€`})` : "Não"}
                          </p>
                        </div>
                        {/* First holder */}
                        <div className="bg-[#F8FAFC] border border-slate-200 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wider text-[#0072C6] mb-2">Primeiro Titular</p>
                          <p className="text-sm text-slate-700">{selProcess.first_holder?.name}</p>
                          <p className="text-xs text-slate-500">{selProcess.first_holder?.email} · {selProcess.first_holder?.phone} · NIF {selProcess.first_holder?.nif}</p>
                          <p className="text-xs text-slate-500 capitalize">Situação: {selProcess.first_holder?.situation === "recibos" ? "Recibos Verdes" : "Contrato de Trabalho"}</p>
                        </div>
                        {/* Second holder */}
                        {selProcess.second_holder?.has && (
                          <div className="bg-[#F8FAFC] border border-slate-200 p-4">
                            <p className="text-xs font-semibold uppercase tracking-wider text-[#0072C6] mb-2">Segundo Titular</p>
                            <p className="text-sm text-slate-700">{selProcess.second_holder?.name}</p>
                            <p className="text-xs text-slate-500">{selProcess.second_holder?.email} · {selProcess.second_holder?.phone} · NIF {selProcess.second_holder?.nif}</p>
                            <p className="text-xs text-slate-500 capitalize">Situação: {selProcess.second_holder?.situation === "recibos" ? "Recibos Verdes" : "Contrato de Trabalho"}</p>
                          </div>
                        )}
                        {/* Documents */}
                        <div className="bg-[#F8FAFC] border border-slate-200 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wider text-[#0072C6] mb-2">Documentos ({(selProcess.documents || []).length})</p>
                          {(selProcess.documents || []).length === 0 ? (
                            <p className="text-xs text-slate-400">Sem documentos.</p>
                          ) : (
                            <ul className="space-y-1.5">
                              {(selProcess.documents || []).map((d) => (
                                <li key={d.id} className="flex items-center gap-2 text-xs text-slate-600">
                                  <FileCheck className="w-3.5 h-3.5 text-[#0072C6] shrink-0" />
                                  <span className="flex-1 truncate">{d.name}</span>
                                  <span className="text-slate-400">{(d.size / 1024).toFixed(0)} KB</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}

                    {/* History */}
                    <div className="mt-6">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Histórico de estados</p>
                      <ul className="space-y-2">
                        {(selProcess.status_history || []).map((h, i) => (
                          <li key={i} className="text-xs text-slate-600 flex flex-wrap gap-x-3">
                            <span className="font-semibold text-[#0072C6]">{h.status}</span>
                            <span className="text-slate-400">{new Date(h.at).toLocaleString("pt-PT")}</span>
                            <span className="text-slate-400">por {h.by}</span>
                            {h.note && <span className="text-slate-500 italic">"{h.note}"</span>}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Messages */}
                    <div className="mt-6 pt-5 border-t border-slate-100">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Mensagens</p>
                      <div className="space-y-2 max-h-52 overflow-y-auto mb-3" data-testid="admin-process-messages">
                        {(selProcess.messages || []).map((m) => (
                          <div key={m.id} className={`text-xs p-3 ${m.internal ? "bg-amber-50 border border-amber-200 text-amber-800" : m.sender === "equipa" ? "bg-[#0072C6] text-white" : "bg-[#F8FAFC] border border-slate-200"}`}>
                            <p className="opacity-70 mb-1">{m.internal ? "Nota interna" : m.sender_name} · {new Date(m.created_at).toLocaleString("pt-PT")}</p>
                            {m.body}
                          </div>
                        ))}
                      </div>
                      <form onSubmit={sendProcMessage} className="flex gap-2 items-center">
                        <input data-testid="proc-msg" value={procMsg} onChange={(e) => setProcMsg(e.target.value)} placeholder="Mensagem ao cliente…"
                          className="flex-1 border border-slate-200 px-3 py-2.5 text-sm" />
                        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
                          <input type="checkbox" checked={procInternal} onChange={(e) => setProcInternal(e.target.checked)} className="w-3.5 h-3.5 accent-[#0072C6]" />
                          Interna
                        </label>
                        <button type="submit" data-testid="proc-msg-send" className="bg-[#0072C6] text-white text-sm font-semibold px-4 py-2.5 hover:bg-[#005FA3]">Enviar</button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DOCUMENTS */}
        {tab === "documents" && (
          <div data-testid="admin-documents">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Documentos de Clientes</h2>
            {documents.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">Ainda não há documentos.</div>
            ) : (
              <div className="space-y-3">
                {documents.map((d) => (
                  <div key={d.id} className="bg-white border border-slate-200 p-4 flex flex-wrap items-center gap-4" data-testid={`admin-doc-${d.id}`}>
                    <FileCheck className="w-5 h-5 text-[#0072C6] shrink-0" />
                    <div className="flex-1 min-w-[200px]">
                      <p className="text-sm font-medium text-[#0072C6]">{d.original_filename}</p>
                      <p className="text-xs text-slate-400">{d.category} · {new Date(d.created_at).toLocaleString("pt-PT")} · {(d.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 ${
                      d.status === "aprovado" ? "bg-emerald-50 text-emerald-700" :
                      d.status === "rejeitado" ? "bg-red-50 text-red-700" :
                      d.status === "reenviar" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"
                    }`}>{d.status}</span>
                    <div className="flex items-center gap-1.5">
                      {DOC_REVIEW.map((s) => (
                        <button key={s} onClick={() => reviewDoc(d, s)} data-testid={`doc-review-${s}-${d.id}`}
                          className={`text-[11px] font-semibold px-2.5 py-1.5 border transition-colors ${
                            s === "aprovado" ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" :
                            s === "rejeitado" ? "border-red-200 text-red-600 hover:bg-red-50" :
                            "border-blue-200 text-blue-600 hover:bg-blue-50"
                          }`}>
                          {s === "aprovado" ? "Aprovar" : s === "rejeitado" ? "Rejeitar" : "Reenviar"}
                        </button>
                      ))}
                      <button onClick={() => deleteDocument(d)} data-testid={`doc-delete-${d.id}`}
                        className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-colors" title="Eliminar documento">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CLIENTS */}
        {tab === "clients" && (
          <div data-testid="admin-clients">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Clientes Registados</h2>
            <form onSubmit={createClient} className="bg-white border border-slate-200 p-5 mb-6 grid sm:grid-cols-4 gap-3" data-testid="create-client-form">
              <input data-testid="nc-name" required placeholder="Nome completo" value={newClient.name} onChange={(e) => setNewClient((c) => ({ ...c, name: e.target.value }))}
                className="border border-slate-200 px-4 py-2.5 text-sm" />
              <input data-testid="nc-email" required type="email" placeholder="Email" value={newClient.email} onChange={(e) => setNewClient((c) => ({ ...c, email: e.target.value }))}
                className="border border-slate-200 px-4 py-2.5 text-sm" />
              <input data-testid="nc-password" required minLength={8} type="text" placeholder="Palavra-passe inicial" value={newClient.password} onChange={(e) => setNewClient((c) => ({ ...c, password: e.target.value }))}
                className="border border-slate-200 px-4 py-2.5 text-sm" />
              <button type="submit" data-testid="nc-submit" className="bg-[#0072C6] text-white text-sm font-semibold px-4 py-2.5 hover:bg-[#005FA3]">Criar Cliente</button>
            </form>
            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm" data-testid="clients-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3.5">Cliente</th>
                    <th className="px-5 py-3.5">Contacto</th>
                    <th className="px-5 py-3.5">Estado</th>
                    <th className="px-5 py-3.5 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {clients.map((c) => (
                    <tr key={c._id} data-testid={`client-row-${c._id}`}>
                      <td className="px-5 py-3.5">
                        <p className="font-medium text-[#0072C6]">{c.name}</p>
                        <p className="text-xs text-slate-400">desde {new Date(c.created_at).toLocaleDateString("pt-PT")}</p>
                      </td>
                      <td className="px-5 py-3.5 text-slate-600 text-xs">{c.email}<br />{c.phone || "—"}</td>
                      <td className="px-5 py-3.5">
                        {c.disabled ? <span className="text-[11px] font-semibold bg-slate-100 text-slate-500 px-2 py-1">Desativada</span> :
                         c.blocked ? <span className="text-[11px] font-semibold bg-red-50 text-red-700 px-2 py-1">Bloqueada</span> :
                         c.accepted ? <span className="text-[11px] font-semibold bg-emerald-50 text-emerald-700 px-2 py-1">Aceite</span> :
                         <span className="text-[11px] font-semibold bg-amber-50 text-amber-700 px-2 py-1">Pendente</span>}
                      </td>
                      <td className="px-5 py-3.5 text-right whitespace-nowrap">
                        {!c.accepted && (
                          <button onClick={() => acceptClient(c)} data-testid={`client-accept-${c._id}`} title="Aceitar cliente"
                            className="p-2 text-slate-500 hover:text-emerald-600">
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => toggleClient(c, "block")} data-testid={`client-block-${c._id}`} title={c.blocked ? "Desbloquear" : "Bloquear"}
                          className={`p-2 ${c.blocked ? "text-emerald-600" : "text-slate-500 hover:text-red-600"}`}>
                          {c.blocked ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </button>
                        <button onClick={() => deleteClient(c)} data-testid={`client-delete-${c._id}`} title="Eliminar cliente"
                          className="p-2 text-slate-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* STAFF (superadmin) */}
        {tab === "staff" && me?.role === "superadmin" && (
          <div data-testid="admin-staff">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Equipa e Permissões</h2>
            <form onSubmit={createStaff} className="bg-white border border-slate-200 p-5 mb-6 grid sm:grid-cols-5 gap-3" data-testid="create-staff-form">
              <input data-testid="ns-name" required placeholder="Nome" value={newStaff.name} onChange={(e) => setNewStaff((s) => ({ ...s, name: e.target.value }))}
                className="border border-slate-200 px-4 py-2.5 text-sm" />
              <input data-testid="ns-email" required type="email" placeholder="Email" value={newStaff.email} onChange={(e) => setNewStaff((s) => ({ ...s, email: e.target.value }))}
                className="border border-slate-200 px-4 py-2.5 text-sm" />
              <input data-testid="ns-password" required minLength={8} type="text" placeholder="Palavra-passe" value={newStaff.password} onChange={(e) => setNewStaff((s) => ({ ...s, password: e.target.value }))}
                className="border border-slate-200 px-4 py-2.5 text-sm" />
              <select data-testid="ns-role" value={newStaff.role} onChange={(e) => setNewStaff((s) => ({ ...s, role: e.target.value }))}
                className="border border-slate-200 px-4 py-2.5 text-sm bg-white">
                <option value="funcionario">Funcionário</option>
                <option value="admin">Administrador</option>
              </select>
              <button type="submit" data-testid="ns-submit" className="bg-[#0072C6] text-white text-sm font-semibold px-4 py-2.5 hover:bg-[#005FA3]">Criar</button>
            </form>
            <div className="bg-white border border-slate-200">
              <ul className="divide-y divide-slate-100" data-testid="staff-list">
                {staff.map((s) => (
                  <li key={s._id} className="px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid={`staff-row-${s._id}`}>
                    <div>
                      <p className="text-sm font-medium text-[#0072C6]">{s.name} <span className="text-xs font-normal text-slate-400">({s.role})</span></p>
                      <p className="text-xs text-slate-400">{s.email}</p>
                    </div>
                    {s.role !== "superadmin" && (
                      <div className="flex items-center gap-1.5">
                        {!s.accepted && (
                          <button onClick={() => acceptStaff(s)} data-testid={`staff-accept-${s._id}`}
                            className="text-xs font-semibold px-3 py-1.5 border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors">
                            Aceitar
                          </button>
                        )}
                        <button onClick={() => toggleStaff(s)} data-testid={`staff-block-${s._id}`}
                          className={`text-xs font-semibold px-3 py-1.5 border transition-colors ${s.blocked ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-red-200 text-red-600 hover:bg-red-50"}`}>
                          {s.blocked ? "Desbloquear" : "Bloquear"}
                        </button>
                        <button onClick={() => deleteStaff(s)} data-testid={`staff-delete-${s._id}`}
                          className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-colors" title="Eliminar membro">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* AUDIT */}
        {tab === "audit" && (
          <div data-testid="admin-audit">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className="font-display text-2xl text-[#0072C6]">Registo de Atividade</h2>
              {me?.role === "superadmin" && auditLog.length > 0 && (
                <div className="flex gap-2">
                  <button onClick={deleteAuditMany} data-testid="audit-delete-selected" disabled={selectedAudit.length === 0}
                    className="text-xs font-semibold px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40">
                    Eliminar selecionados ({selectedAudit.length})
                  </button>
                  <button onClick={deleteAuditAll} data-testid="audit-delete-all"
                    className="text-xs font-semibold px-4 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors">
                    Eliminar tudo
                  </button>
                </div>
              )}
            </div>
            <div className="bg-white border border-slate-200">
              {auditLog.length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-400">Sem registos ainda.</p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="audit-list">
                  {auditLog.map((a) => (
                    <li key={a.id} className="px-5 py-3.5 text-xs flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`audit-row-${a.id}`}>
                      {me?.role === "superadmin" && (
                        <input type="checkbox" data-testid={`audit-select-${a.id}`} checked={selectedAudit.includes(a.id)} onChange={() => toggleAuditSelect(a.id)}
                          className="w-3.5 h-3.5 accent-[#0072C6]" />
                      )}
                      <span className="font-semibold text-[#0072C6] w-36">{a.action}</span>
                      <span className="text-slate-500">{a.actor_email} <span className="text-slate-300">({a.actor_role})</span></span>
                      <span className="text-slate-400">{a.entity}{a.entity_id ? ` ${a.entity_id.slice(0, 8)}` : ""} {a.detail}</span>
                      <span className="text-slate-400 ml-auto">{new Date(a.created_at).toLocaleString("pt-PT")}</span>
                      {me?.role === "superadmin" && (
                        <button onClick={() => deleteAuditOne(a.id)} data-testid={`audit-delete-${a.id}`} title="Eliminar registo"
                          className="p-1 text-slate-300 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* LEADS (website forms) */}
        {tab === "leads" && (
          <div data-testid="admin-leads">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Pedidos de Clientes</h2>
            {leads.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">
                Ainda não há pedidos. Os formulários do website chegam aqui.
              </div>
            ) : (
              <div className="space-y-3">
                {leads.map((l) => (
                  <div key={l.id} className="bg-white border border-slate-200 p-5" data-testid={`lead-card-${l.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#0072C6]">{l.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          <span className="uppercase tracking-wider font-semibold text-[#0072C6]">{l.inquiry_type}</span>
                          {" · "}{l.email} · {l.phone}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">
                          {new Date(l.created_at).toLocaleString("pt-PT")}
                        </span>
                        <div className="flex items-center gap-1">
                          <button onClick={() => leadAction(l, "accept")} data-testid={`lead-accept-${l.id}`} title="Aceitar"
                            className="p-1.5 text-slate-400 hover:text-emerald-600 border border-slate-200 hover:border-emerald-200 transition-colors">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => leadAction(l, "reject")} data-testid={`lead-reject-${l.id}`} title="Recusar"
                            className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-colors">
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => leadAction(l, "delete")} data-testid={`lead-delete-${l.id}`} title="Eliminar"
                            className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                    {l.message && <p className="mt-3 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{l.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TESTIMONIALS */}
        {tab === "testimonials" && (
          <div data-testid="admin-testimonials">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Testemunhos de Clientes</h2>
            {testimonials.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">
                Ainda não há testemunhos. Os testemunhos submetidos no website chegam aqui para aprovação.
              </div>
            ) : (
              <div className="space-y-3">
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-white border border-slate-200 p-5" data-testid={`testimonial-admin-card-${t.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#0072C6]">
                          {t.name}
                          {t.location && <span className="text-xs text-slate-500 font-normal"> · {t.location}</span>}
                        </p>
                        <p className="mt-1">
                          <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 ${t.approved ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
                            data-testid={`testimonial-status-${t.id}`}>
                            {t.approved ? "Aprovado" : "Pendente"}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">
                          {new Date(t.created_at).toLocaleString("pt-PT")}
                        </span>
                        <div className="flex items-center gap-1">
                          {!t.approved && (
                            <button onClick={() => testimonialAction(t, "approve")} data-testid={`testimonial-approve-${t.id}`} title="Aprovar"
                              className="p-1.5 text-slate-400 hover:text-emerald-600 border border-slate-200 hover:border-emerald-200 transition-colors">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {t.approved && (
                            <button onClick={() => testimonialAction(t, "reject")} data-testid={`testimonial-reject-${t.id}`} title="Rejeitar / ocultar"
                              className="p-1.5 text-slate-400 hover:text-amber-600 border border-slate-200 hover:border-amber-200 transition-colors">
                              <Ban className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button onClick={() => testimonialAction(t, "delete")} data-testid={`testimonial-delete-${t.id}`} title="Eliminar"
                            className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">“{t.text}”</p>
                    <div className="flex items-center gap-1 mt-2" data-testid={`testimonial-stars-${t.id}`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-3.5 h-3.5 ${n <= (t.rating || 5) ? "fill-[#FF6A00] text-[#FF6A00]" : "text-slate-300"}`} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* VENDEDORES */}
        {tab === "sellers" && (
          <div data-testid="admin-sellers">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Gestão de Vendedores</h2>
            {sellers.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">
                Ainda não há candidatos a vendedor. As candidaturas submetidas no site aparecem aqui.
              </div>
            ) : (
              <div className="space-y-3 mb-12">
                {sellers.map((s) => {
                  const sellerDeals = deals.filter((d) => d.seller_id === s.id);
                  const totalMargin = sellerDeals.filter((d) => d.status === "vendida").reduce((sum, d) => sum + (d.margin || 0), 0);
                  const status = s.blocked ? { label: "Bloqueado", cls: "bg-red-50 text-red-700" } : s.accepted ? { label: "Aprovado", cls: "bg-emerald-50 text-emerald-700" } : { label: "Pendente", cls: "bg-amber-50 text-amber-700" };
                  return (
                    <div key={s.id} className="bg-white border border-slate-200 p-5" data-testid={`seller-card-${s.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          {s.photo_url ? (
                            <img src={s.photo_url} alt={s.name} className="w-14 h-14 object-cover border border-slate-200" />
                          ) : (
                            <div className="w-14 h-14 bg-slate-100 flex items-center justify-center text-slate-300"><Users className="w-6 h-6" /></div>
                          )}
                          <div>
                            <p className="font-semibold text-[#0072C6]">{s.name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{s.email} · {s.phone}{s.whatsapp ? ` · WhatsApp ${s.whatsapp}` : ""}</p>
                            {s.address && <p className="text-xs text-slate-400 mt-0.5">{s.address}</p>}
                            <p className="mt-1.5">
                              <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 ${status.cls}`} data-testid={`seller-status-${s.id}`}>{status.label}</span>
                              <span className="text-xs text-slate-400 ml-2">{sellerDeals.length} negociação(ões) · Margem {new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(totalMargin)}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-xs text-slate-400">{new Date(s.created_at).toLocaleString("pt-PT")}</span>
                          <div className="flex items-center gap-1">
                            {!s.accepted && (
                              <button onClick={() => sellerAction(s, "accept")} data-testid={`seller-accept-${s.id}`} title="Aceitar"
                                className="p-1.5 text-slate-400 hover:text-emerald-600 border border-slate-200 hover:border-emerald-200 transition-colors">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {!s.accepted && (
                              <button onClick={() => sellerAction(s, "reject")} data-testid={`seller-reject-${s.id}`} title="Recusar"
                                className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-colors">
                                <Ban className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => sellerAction(s, "block")} data-testid={`seller-block-${s.id}`} title={s.blocked ? "Desbloquear" : "Bloquear"}
                              className="p-1.5 text-slate-400 hover:text-amber-600 border border-slate-200 hover:border-amber-200 transition-colors">
                              {s.blocked ? <RotateCcw className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                            </button>
                            <button onClick={() => sellerAction(s, "delete")} data-testid={`seller-delete-${s.id}`} title="Eliminar"
                              className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {s.presentation && <p className="mt-3 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{s.presentation}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            <h3 className="font-display text-xl text-[#0072C6] mb-5">Negociações dos Vendedores</h3>
            {deals.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">Ainda não há negociações registadas.</div>
            ) : (
              <div className="space-y-3" data-testid="admin-deals">
                {deals.map((d) => (
                  <div key={d.id} className="bg-white border border-slate-200 p-5" data-testid={`admin-deal-${d.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#0072C6]">{d.vehicle_brand} {d.vehicle_model} <span className="text-slate-400 font-normal text-sm">({d.vehicle_title})</span></p>
                        <p className="text-xs text-slate-500 mt-0.5">Vendedor: {d.seller_name} · Cliente: {d.client_name} ({d.client_phone})</p>
                        <p className="text-xs text-slate-500 mt-1.5">
                          Base {new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(d.base_price)} + Margem {new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(d.margin)} = <span className="font-semibold text-emerald-600">{new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(d.final_price)}</span>
                        </p>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1.5">
                        <span className="text-xs uppercase tracking-wider font-semibold text-[#0072C6]">{d.status}</span>
                        <p className="text-xs text-slate-400">{new Date(d.created_at).toLocaleDateString("pt-PT")}</p>
                        <p className="text-xs text-slate-400">{(d.documents || []).length} doc(s)</p>
                        {d.status === "vendida" && (
                          <button onClick={() => toggleCommission(d)} data-testid={`admin-commission-${d.id}`}
                            className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border transition-colors ${d.commission_paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            {d.commission_paid ? "Comissão paga" : "Comissão por pagar"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* VENDAS (relatório) */}
        {tab === "sales" && (
          <div data-testid="admin-sales">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Relatório de Vendas e Comissões</h2>
            {(() => {
              const sold = deals.filter((d) => d.status === "vendida");
              const now = new Date();
              const thisMonth = sold.filter((d) => { const dt = new Date(d.created_at); return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear(); });
              const totalRevenue = sold.reduce((s, d) => s + (d.final_price || 0), 0);
              const totalMargin = sold.reduce((s, d) => s + (d.margin || 0), 0);
              const paid = sold.filter((d) => d.commission_paid).reduce((s, d) => s + (d.margin || 0), 0);
              const pending = totalMargin - paid;
              const bySeller = {};
              sold.forEach((d) => {
                if (!bySeller[d.seller_id]) bySeller[d.seller_id] = { name: d.seller_name, count: 0, revenue: 0, margin: 0, paid: 0 };
                bySeller[d.seller_id].count += 1;
                bySeller[d.seller_id].revenue += d.final_price || 0;
                bySeller[d.seller_id].margin += d.margin || 0;
                if (d.commission_paid) bySeller[d.seller_id].paid += d.margin || 0;
              });
              const sellersList = Object.values(bySeller);
              return (
                <>
                  <div className="grid sm:grid-cols-4 gap-5 mb-10">
                    <div className="bg-white border border-slate-200 p-6" data-testid="sales-count">
                      <p className="text-xs uppercase tracking-wider text-slate-500">Vendas totais</p>
                      <p className="mt-2 font-display text-3xl text-[#0072C6]">{sold.length}</p>
                      <p className="text-xs text-slate-400 mt-1">{thisMonth.length} este mês</p>
                    </div>
                    <div className="bg-white border border-slate-200 p-6" data-testid="sales-revenue">
                      <p className="text-xs uppercase tracking-wider text-slate-500">Faturação</p>
                      <p className="mt-2 font-display text-3xl text-[#0072C6]">{fmtEur(totalRevenue)}</p>
                    </div>
                    <div className="bg-white border border-slate-200 p-6" data-testid="sales-margin">
                      <p className="text-xs uppercase tracking-wider text-slate-500">Comissões totais</p>
                      <p className="mt-2 font-display text-3xl text-emerald-600">{fmtEur(totalMargin)}</p>
                    </div>
                    <div className="bg-white border border-slate-200 p-6" data-testid="sales-pending">
                      <p className="text-xs uppercase tracking-wider text-slate-500">Por pagar</p>
                      <p className="mt-2 font-display text-3xl text-amber-600">{fmtEur(pending)}</p>
                      <p className="text-xs text-slate-400 mt-1">Pago {fmtEur(paid)}</p>
                    </div>
                  </div>

                  <h3 className="font-display text-xl text-[#0072C6] mb-5">Por vendedor</h3>
                  {sellersList.length === 0 ? (
                    <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">Ainda não há vendas registadas.</div>
                  ) : (
                    <div className="space-y-3" data-testid="sales-by-seller">
                      {sellersList.map((s) => (
                        <div key={s.name} className="bg-white border border-slate-200 p-5 flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-[#0072C6]">{s.name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{s.count} venda(s)</p>
                          </div>
                          <div className="flex items-center gap-6 text-right">
                            <div>
                              <p className="text-xs text-slate-400">Faturação</p>
                              <p className="font-semibold text-sm text-[#0072C6]">{fmtEur(s.revenue)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400">Comissão</p>
                              <p className="font-semibold text-sm text-emerald-600">{fmtEur(s.margin)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400">Por pagar</p>
                              <p className="font-semibold text-sm text-amber-600">{fmtEur(s.margin - s.paid)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* GESTÃO DE PÁGINAS */}
        {tab === "pages" && settings && (
          <form onSubmit={saveSettings} data-testid="admin-pages-form" className="max-w-3xl">
            <h2 className="font-display text-2xl text-[#0072C6] mb-2">Gestão de Páginas</h2>
            <p className="text-sm text-slate-500 mb-6">Edite o conteúdo das páginas do website. Clique em <strong>Guardar Alterações</strong> no fim para publicar.</p>

            {/* Quem Somos */}
            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Quem Somos
              </h3>
              <div className="space-y-5">
                <div>
                  <label htmlFor="p-about-title" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Título</label>
                  <input id="p-about-title" data-testid="pages-about-title" value={settings.about_title || ""} onChange={setS("about_title")}
                    placeholder="Porquê a SOLUTION CAR PREMIUM" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="p-about-text" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Texto / Descrição</label>
                  <textarea id="p-about-text" data-testid="pages-about-text" rows={5} value={settings.about_text || ""} onChange={setS("about_text")}
                    placeholder="Descreva a empresa, os serviços e os valores…" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Imagens</label>
                  <div className="flex flex-wrap gap-3 mb-3" data-testid="pages-about-images">
                    {(settings.about_images || []).map((url, i) => (
                      <div key={i} className="relative group" data-testid={`pages-about-image-${i}`}>
                        <img src={url} alt={`Sobre ${i + 1}`} className="w-24 h-24 object-cover border border-slate-200" />
                        <button type="button" onClick={() => removeAboutImage(i)} data-testid={`pages-about-image-remove-${i}`}
                          className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <label className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-sm text-slate-600 cursor-pointer hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                    {galleryUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                    Adicionar imagem
                    <input type="file" accept="image/*" className="hidden" onChange={addAboutImage} data-testid="pages-about-image-upload" />
                  </label>
                </div>
              </div>
            </div>

            {/* Documentos Legais */}
            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6" data-testid="legal-docs-section">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Páginas e Documentos Legais
              </h3>
              <div className="flex flex-wrap gap-2 mb-6" data-testid="legal-doc-tabs">
                {LEGAL_DOCS.map((d) => (
                  <button key={d.key} type="button" data-testid={`legal-tab-${d.key}`}
                    onClick={() => { setLegalDocKey(d.key); setLegalPreview(false); }}
                    className={`px-4 py-2 text-xs font-semibold border transition-colors ${legalDocKey === d.key ? "bg-[#0072C6] text-white border-[#0072C6]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0072C6]"}`}>
                    {d.label}
                  </button>
                ))}
              </div>

              <div className="space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 ${currentLegalDoc.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="legal-status">
                      {currentLegalDoc.status === "published" ? "Publicado" : "Rascunho"}
                    </span>
                    {currentLegalDoc.updated_at && (
                      <span className="text-xs text-slate-400 ml-3" data-testid="legal-updated">
                        Atualizado: {new Date(currentLegalDoc.updated_at).toLocaleString("pt-PT")}
                      </span>
                    )}
                  </div>
                  <a href={LEGAL_DOCS.find((d) => d.key === legalDocKey)?.path} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-[#0072C6] underline" data-testid="legal-view-page">
                    Ver página pública
                  </a>
                </div>

                <div>
                  <label htmlFor="legal-title" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Título do documento</label>
                  <input id="legal-title" data-testid="legal-title" value={currentLegalDoc.title} onChange={(e) => setLegalDoc({ title: e.target.value })}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>

                <div>
                  <label htmlFor="legal-content" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                    Conteúdo <span className="text-slate-400 normal-case font-normal">(use "# Título", "## Subtítulo", "- item" para formatar)</span>
                  </label>
                  {legalPreview ? (
                    <div className="border border-slate-200 px-4 py-3 min-h-[300px] bg-[#F8FAFC]" data-testid="legal-preview">
                      {(currentLegalDoc.content || "").split(/\n{2,}/).map((b, i) => (
                        <p key={i} className="text-sm text-slate-600 mb-3 whitespace-pre-line">{b}</p>
                      ))}
                    </div>
                  ) : (
                    <textarea id="legal-content" data-testid="legal-content" rows={14} value={currentLegalDoc.content} onChange={(e) => setLegalDoc({ content: e.target.value })}
                      placeholder="Cole ou escreva aqui o documento completo…"
                      className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y font-mono" />
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={() => setLegalPreview(!legalPreview)} data-testid="legal-preview-btn"
                    className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 text-sm font-semibold px-5 py-2.5 hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                    {legalPreview ? "Editar" : "Pré-visualizar"}
                  </button>
                  <button type="button" onClick={() => saveLegalDoc(false)} disabled={legalSaving} data-testid="legal-save-draft"
                    className="inline-flex items-center gap-2 bg-slate-600 hover:bg-slate-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                    {legalSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Guardar como Rascunho
                  </button>
                  <button type="button" onClick={() => saveLegalDoc(true)} disabled={legalSaving} data-testid="legal-publish"
                    className="inline-flex items-center gap-2 bg-[#FF6A00] hover:bg-[#e65f00] disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                    {legalSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Publicar
                  </button>
                </div>
              </div>
            </div>

            {/* Testemunhos (secção) */}
            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <MessageSquareQuote className="w-4 h-4" /> Secção de Testemunhos
              </h3>
              <div className="space-y-5">
                <div>
                  <label htmlFor="p-test-title" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Título</label>
                  <input id="p-test-title" data-testid="pages-testimonials-title" value={settings.testimonials_title || ""} onChange={setS("testimonials_title")}
                    placeholder="O Que Dizem os Nossos Clientes" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="p-test-desc" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Descrição</label>
                  <textarea id="p-test-desc" data-testid="pages-testimonials-description" rows={3} value={settings.testimonials_description || ""} onChange={setS("testimonials_description")}
                    placeholder="Texto introdutório da secção de testemunhos…" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y" />
                </div>
              </div>
            </div>

            {/* Galeria de Clientes */}
            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <ImagePlus className="w-4 h-4" /> Galeria de Clientes
              </h3>
              <div className="space-y-5 mb-8">
                <div>
                  <label htmlFor="p-gal-title" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Título da galeria</label>
                  <input id="p-gal-title" data-testid="pages-gallery-title" value={settings.gallery_title || ""} onChange={setS("gallery_title")}
                    placeholder="Galeria de Clientes" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="p-gal-desc" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Descrição da galeria</label>
                  <textarea id="p-gal-desc" data-testid="pages-gallery-description" rows={2} value={settings.gallery_description || ""} onChange={setS("gallery_description")}
                    placeholder="Texto introdutório da galeria…" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y" />
                </div>
              </div>

              {/* Add / edit form */}
              <div className="border border-slate-200 p-5 mb-6 bg-[#F8FAFC]" data-testid="gallery-item-form" onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault(); }}>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-4">
                  {editingGalleryId ? "Editar publicação" : "Adicionar publicação"}
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Fotografia ou vídeo *</label>
                    {galleryForm.media_url && (
                      <div className="mb-3" data-testid="gallery-preview">
                        {galleryForm.media_type === "video" ? (
                          <video src={galleryForm.media_url} controls className="w-48 h-32 object-cover border border-slate-200 bg-black" />
                        ) : (
                          <img src={galleryForm.media_url} alt="Pré-visualização" className="w-48 h-32 object-cover border border-slate-200" />
                        )}
                      </div>
                    )}
                    <label className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-sm text-slate-600 cursor-pointer hover:border-[#0072C6] hover:text-[#0072C6] transition-colors bg-white">
                      {galleryUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {galleryForm.media_url ? "Substituir ficheiro" : "Carregar fotografia ou vídeo"}
                      <input type="file" accept="image/*,video/*" className="hidden" onChange={handleGalleryUpload} data-testid="gallery-media-upload" />
                    </label>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="g-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nome do cliente</label>
                      <input id="g-name" data-testid="gallery-name" value={galleryForm.name} onChange={(e) => setGalleryForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Ex.: João Silva" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] bg-white" />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <input type="checkbox" data-testid="gallery-published" checked={galleryForm.published} onChange={(e) => setGalleryForm((f) => ({ ...f, published: e.target.checked }))}
                          className="w-4 h-4 accent-[#0072C6]" />
                        Publicar no site
                      </label>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="g-desc" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Descrição / Depoimento</label>
                    <textarea id="g-desc" data-testid="gallery-description" rows={3} value={galleryForm.description} onChange={(e) => setGalleryForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Escreva uma descrição ou o depoimento do cliente…" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y bg-white" />
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={saveGalleryItem} data-testid="gallery-save"
                      className="inline-flex items-center gap-2 bg-[#0072C6] hover:bg-[#005FA3] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                      {editingGalleryId ? "Atualizar publicação" : "Adicionar à galeria"}
                    </button>
                    {editingGalleryId && (
                      <button type="button" onClick={resetGalleryForm} data-testid="gallery-cancel"
                        className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 text-sm font-semibold px-5 py-2.5 hover:border-slate-400 transition-colors">
                        Cancelar
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Items list */}
              {galleryItems.length === 0 ? (
                <div className="border border-dashed border-slate-300 p-8 text-center text-slate-400 text-sm" data-testid="gallery-empty">
                  Ainda não há publicações na galeria.
                </div>
              ) : (
                <div className="space-y-3" data-testid="gallery-list">
                  {galleryItems.map((g) => (
                    <div key={g.id} className="flex flex-wrap items-center gap-4 border border-slate-200 p-4 bg-white" data-testid={`gallery-item-${g.id}`}>
                      {g.media_type === "video" ? (
                        <video src={g.media_url} className="w-20 h-14 object-cover border border-slate-200 bg-black" />
                      ) : (
                        <img src={g.media_url} alt={g.name || "Cliente"} className="w-20 h-14 object-cover border border-slate-200" />
                      )}
                      <div className="flex-1 min-w-[160px]">
                        <p className="font-semibold text-sm text-[#0072C6]">{g.name || <span className="text-slate-400 font-normal">Sem nome</span>}</p>
                        {g.description && <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{g.description}</p>}
                        <span className={`inline-block mt-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 ${g.published ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                          data-testid={`gallery-status-${g.id}`}>
                          {g.published ? "Publicado" : "Oculto"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => toggleGalleryPublished(g)} data-testid={`gallery-toggle-${g.id}`} title={g.published ? "Ocultar" : "Publicar"}
                          className="p-1.5 text-slate-400 hover:text-[#0072C6] border border-slate-200 hover:border-[#0072C6] transition-colors">
                          {g.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                        <button type="button" onClick={() => editGalleryItem(g)} data-testid={`gallery-edit-${g.id}`} title="Editar"
                          className="p-1.5 text-slate-400 hover:text-[#0072C6] border border-slate-200 hover:border-[#0072C6] transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => deleteGalleryItem(g)} data-testid={`gallery-delete-${g.id}`} title="Eliminar"
                          className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="sticky bottom-4 z-[70]">
              <button type="button" onClick={saveSettings} disabled={settingsSaving} data-testid="pages-save"
                className="inline-flex items-center gap-2 bg-[#FF6A00] hover:bg-[#e65f00] disabled:opacity-60 text-white text-sm font-semibold px-8 py-3.5 shadow-lg transition-colors">
                {settingsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {settingsSaving ? "A guardar…" : "Guardar Alterações"}
              </button>
            </div>
          </form>
        )}

        {/* PUBLICIDADE */}
        {tab === "ads" && (
          <div data-testid="admin-ads">
            <h2 className="font-display text-2xl text-[#0072C6] mb-2">Gestão de Publicidade / Banners</h2>
            <p className="text-sm text-slate-500 mb-6">Crie e gira os anúncios que aparecem no carrossel do topo da página inicial.</p>

            {/* Form */}
            <form onSubmit={saveAd} className="bg-white border border-slate-200 p-6 sm:p-8 mb-8" data-testid="ad-form">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <Megaphone className="w-4 h-4" /> {editingAdId ? "Editar anúncio" : "Novo anúncio"}
              </h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="ad-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nome interno da campanha *</label>
                  <input id="ad-name" data-testid="ad-name" required minLength={2} value={adForm.name} onChange={(e) => setAdForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="Ex.: Campanha Verão" />
                </div>
                <div>
                  <label htmlFor="ad-type" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Tipo de conteúdo *</label>
                  <select id="ad-type" data-testid="ad-type" value={adForm.content_type} onChange={(e) => setAdForm((f) => ({ ...f, content_type: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] bg-white">
                    <option value="image">Imagem</option>
                    <option value="video">Vídeo</option>
                    <option value="youtube">YouTube</option>
                    <option value="link">Link externo</option>
                  </select>
                </div>

                {/* Media */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                    {adForm.content_type === "youtube" ? "URL do YouTube *" : adForm.content_type === "link" ? "URL da imagem *" : "Imagem ou vídeo *"}
                  </label>
                  {adForm.content_type === "image" || adForm.content_type === "video" ? (
                    <div>
                      {adForm.media_url && (
                        <div className="mb-3" data-testid="ad-media-preview">
                          {adForm.content_type === "video" ? (
                            <video src={adForm.media_url} controls className="w-full max-w-md h-40 object-cover border border-slate-200 bg-black" />
                          ) : (
                            <img src={adForm.media_url} alt="Pré-visualização" className="w-full max-w-md h-40 object-cover border border-slate-200" />
                          )}
                        </div>
                      )}
                      <label className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-sm text-slate-600 cursor-pointer hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                        {adUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {adForm.media_url ? "Substituir ficheiro" : "Carregar imagem ou vídeo"}
                        <input type="file" accept="image/*,video/mp4" className="hidden" onChange={handleAdUpload} data-testid="ad-upload" />
                      </label>
                    </div>
                  ) : (
                    <input id="ad-media-url" data-testid="ad-media-url" value={adForm.media_url} onChange={(e) => setAdForm((f) => ({ ...f, media_url: e.target.value }))}
                      className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]"
                      placeholder={adForm.content_type === "youtube" ? "https://www.youtube.com/watch?v=…" : "https://…"} />
                  )}
                </div>

                <div>
                  <label htmlFor="ad-title" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Título publicitário</label>
                  <input id="ad-title" data-testid="ad-title" value={adForm.title} onChange={(e) => setAdForm((f) => ({ ...f, title: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="Ex.: Oferta Especial" />
                </div>
                <div>
                  <label htmlFor="ad-desc" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Descrição</label>
                  <input id="ad-desc" data-testid="ad-desc" value={adForm.description} onChange={(e) => setAdForm((f) => ({ ...f, description: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="Breve descrição da campanha" />
                </div>

                <div>
                  <label htmlFor="ad-link" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Link de destino</label>
                  <input id="ad-link" data-testid="ad-link" value={adForm.link_url} onChange={(e) => setAdForm((f) => ({ ...f, link_url: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="/viaturas ou https://…" />
                </div>
                <div>
                  <label htmlFor="ad-button" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Texto do botão</label>
                  <input id="ad-button" data-testid="ad-button" value={adForm.button_text} onChange={(e) => setAdForm((f) => ({ ...f, button_text: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="Ex.: Saiba Mais" />
                </div>

                <div>
                  <label htmlFor="ad-start" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Data de início</label>
                  <input id="ad-start" type="datetime-local" data-testid="ad-start" value={adForm.start_date} onChange={(e) => setAdForm((f) => ({ ...f, start_date: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="ad-end" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Data de término</label>
                  <input id="ad-end" type="datetime-local" data-testid="ad-end" value={adForm.end_date} onChange={(e) => setAdForm((f) => ({ ...f, end_date: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>

                <div>
                  <label htmlFor="ad-order" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Ordem de apresentação</label>
                  <input id="ad-order" type="number" min="0" data-testid="ad-order" value={adForm.order} onChange={(e) => setAdForm((f) => ({ ...f, order: Number(e.target.value) }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="ad-duration" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Tempo de exibição (segundos)</label>
                  <input id="ad-duration" type="number" min="1" max="60" data-testid="ad-duration" value={adForm.duration_seconds} onChange={(e) => setAdForm((f) => ({ ...f, duration_seconds: Number(e.target.value) }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>

                <div className="sm:col-span-2 flex flex-wrap gap-6">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input type="checkbox" data-testid="ad-active" checked={adForm.active} onChange={(e) => setAdForm((f) => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-[#0072C6]" />
                    Ativo
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input type="checkbox" data-testid="ad-new-tab" checked={adForm.open_new_tab} onChange={(e) => setAdForm((f) => ({ ...f, open_new_tab: e.target.checked }))} className="w-4 h-4 accent-[#0072C6]" />
                    Abrir link em nova aba
                  </label>
                  {adForm.content_type === "video" && (
                    <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input type="checkbox" data-testid="ad-autoplay" checked={adForm.autoplay_video} onChange={(e) => setAdForm((f) => ({ ...f, autoplay_video: e.target.checked }))} className="w-4 h-4 accent-[#0072C6]" />
                      Iniciar vídeo automaticamente
                    </label>
                  )}
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button type="submit" disabled={adSaving} data-testid="ad-save"
                  className="inline-flex items-center gap-2 bg-[#0072C6] hover:bg-[#005FA3] disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 transition-colors">
                  {adSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {editingAdId ? "Atualizar anúncio" : "Criar anúncio"}
                </button>
                {editingAdId && (
                  <button type="button" onClick={resetAdForm} data-testid="ad-cancel"
                    className="border border-slate-300 text-slate-600 text-sm font-semibold px-6 py-3 hover:border-slate-400 transition-colors">
                    Cancelar
                  </button>
                )}
              </div>
            </form>

            {/* List */}
            {ads.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">Ainda não há anúncios. Crie o primeiro acima.</div>
            ) : (
              <div className="space-y-3" data-testid="ads-list">
                {ads.map((a) => (
                  <div key={a.id} className="bg-white border border-slate-200 p-5 flex flex-wrap items-center gap-4" data-testid={`ad-card-${a.id}`}>
                    <div className="w-24 h-16 bg-slate-100 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                      {a.content_type === "image" && a.media_url ? (
                        <img src={a.media_url} alt={a.name} className="w-full h-full object-cover" />
                      ) : a.content_type === "video" && a.media_url ? (
                        <video src={a.media_url} className="w-full h-full object-cover" />
                      ) : (
                        <Megaphone className="w-6 h-6 text-slate-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-semibold text-[#0072C6]">{a.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5 capitalize">{a.content_type}{a.title ? ` · ${a.title}` : ""}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {a.views || 0} visualizações · {a.clicks || 0} cliques · ordem {a.order} · {a.duration_seconds}s
                      </p>
                      <span className={`inline-block mt-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 ${a.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`ad-status-${a.id}`}>
                        {a.active ? "Ativo" : "Inativo"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => toggleAd(a)} data-testid={`ad-toggle-${a.id}`} title={a.active ? "Desativar" : "Ativar"}
                        className="p-1.5 text-slate-400 hover:text-[#0072C6] border border-slate-200 hover:border-[#0072C6] transition-colors">
                        {a.active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                      <button type="button" onClick={() => editAd(a)} data-testid={`ad-edit-${a.id}`} title="Editar"
                        className="p-1.5 text-slate-400 hover:text-[#0072C6] border border-slate-200 hover:border-[#0072C6] transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => deleteAd(a)} data-testid={`ad-delete-${a.id}`} title="Eliminar"
                        className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PRIVACIDADE (RGPD) */}
        {tab === "privacy" && settings && (
          <div data-testid="admin-privacy">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Gestão de Privacidade e Documentos</h2>

            {/* Editar política */}
            <form onSubmit={saveSettings} className="bg-white border border-slate-200 p-6 sm:p-8 mb-8" data-testid="privacy-policy-form">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <Lock className="w-4 h-4" /> Política de Privacidade (editável)
              </h3>
              <div className="space-y-5">
                <div>
                  <label htmlFor="pv-version" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Versão da política</label>
                  <input id="pv-version" data-testid="privacy-version" value={settings.privacy_policy_version || "1.0"} onChange={setS("privacy_policy_version")}
                    className="w-full max-w-xs border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="pv-text" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Texto da política (uma secção por linha)</label>
                  <textarea id="pv-text" data-testid="privacy-text" rows={8} value={settings.privacy_policy_text || ""} onChange={setS("privacy_policy_text")}
                    placeholder="Deixe vazio para usar o texto-base. Escreva aqui a política validada juridicamente…"
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y" />
                  <p className="text-xs text-slate-400 mt-1.5">Se vazio, é usado o texto-base com a entidade legal Trajetória Lendária Unipessoal Lda (NIPC 519689623). Recomendamos validação jurídica.</p>
                </div>
                <div>
                  <label htmlFor="pv-retention" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Prazo de conservação (dias)</label>
                  <input id="pv-retention" type="number" min="0" data-testid="privacy-retention" value={settings.data_retention_days || 365}
                    onChange={(e) => setSettings((s) => ({ ...s, data_retention_days: Number(e.target.value) }))}
                    className="w-full max-w-xs border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <button type="submit" data-testid="privacy-save"
                  className="inline-flex items-center gap-2 bg-[#FF6A00] hover:bg-[#e65f00] text-white text-sm font-semibold px-6 py-3 transition-colors">
                  Guardar Política
                </button>
              </div>
            </form>

            {/* Pedidos RGPD */}
            <h3 className="font-display text-xl text-[#0072C6] mb-5">Pedidos de Proteção de Dados</h3>
            {privacyRequests.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm mb-10">Ainda não há pedidos de acesso ou eliminação de dados.</div>
            ) : (
              <div className="space-y-3 mb-10" data-testid="privacy-requests">
                {privacyRequests.map((r) => (
                  <div key={r.id} className="bg-white border border-slate-200 p-5" data-testid={`privacy-request-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#0072C6]">{r.name} <span className="text-slate-400 font-normal text-sm">({r.email})</span></p>
                        <p className="text-xs text-slate-500 mt-0.5 capitalize">Tipo: {r.request_type}</p>
                        {r.message && <p className="text-sm text-slate-600 mt-2 border-t border-slate-100 pt-2">{r.message}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 ${r.status === "concluido" ? "bg-emerald-50 text-emerald-700" : r.status === "em_processamento" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                          {r.status === "concluido" ? "Concluído" : r.status === "em_processamento" ? "Em processamento" : "Pendente"}
                        </span>
                        <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString("pt-PT")}</span>
                        <div className="flex gap-1.5">
                          {r.status !== "em_processamento" && r.status !== "concluido" && (
                            <button onClick={() => privacyRequestAction(r, "em_processamento")} data-testid={`privacy-process-${r.id}`} className="text-[10px] uppercase font-semibold px-2 py-1 border border-blue-200 text-blue-700 hover:bg-blue-50">Processar</button>
                          )}
                          {r.status !== "concluido" && (
                            <button onClick={() => privacyRequestAction(r, "concluido")} data-testid={`privacy-complete-${r.id}`} className="text-[10px] uppercase font-semibold px-2 py-1 border border-emerald-200 text-emerald-700 hover:bg-emerald-50">Concluir</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Consentimentos */}
            <h3 className="font-display text-xl text-[#0072C6] mb-5">Registos de Consentimento</h3>
            {consents.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">Ainda não há registos de consentimento.</div>
            ) : (
              <div className="space-y-3" data-testid="privacy-consents">
                {consents.map((c) => (
                  <div key={c.id} className="bg-white border border-slate-200 p-5 flex flex-wrap items-start justify-between gap-3" data-testid={`consent-${c.id}`}>
                    <div>
                      <p className="font-semibold text-[#0072C6]">{c.name || "—"} <span className="text-slate-400 font-normal text-sm">({c.email})</span></p>
                      <p className="text-xs text-slate-500 mt-0.5">Contexto: {c.context} · Versão {c.policy_version}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Política: <span className={c.accept_policy ? "text-emerald-600 font-semibold" : "text-red-600"}>{c.accept_policy ? "Aceite" : "Não aceite"}</span>
                        {" · "}Marketing: <span className={c.accept_marketing ? "text-emerald-600 font-semibold" : "text-slate-500"}>{c.accept_marketing ? "Aceite" : "Não"}</span>
                      </p>
                    </div>
                    <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString("pt-PT")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SETTINGS */}
        {tab === "settings" && settings && (
          <form onSubmit={saveSettings} data-testid="admin-settings-form" className="max-w-3xl">
            <h2 className="font-display text-2xl text-[#0072C6] mb-6">Definições do Website</h2>

            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <Phone className="w-4 h-4" /> Contactos
              </h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="s-phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Telefone</label>
                  <input id="s-phone" data-testid="settings-phone" value={settings.phone} onChange={setS("phone")}
                    placeholder="+351 912 345 678" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="s-wa" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">WhatsApp (só números, com indicativo)</label>
                  <input id="s-wa" data-testid="settings-whatsapp" value={settings.whatsapp} onChange={setS("whatsapp")}
                    placeholder="351912345678" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="s-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Email</label>
                  <input id="s-email" data-testid="settings-email" value={settings.email} onChange={setS("email")}
                    placeholder="geral@solutioncarpremium.pt" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="s-address" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> Morada do stand
                  </label>
                  <input id="s-address" data-testid="settings-address" value={settings.address} onChange={setS("address")}
                    placeholder="Rua, nº, código postal, cidade" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <Share2 className="w-4 h-4" /> Redes Sociais
              </h3>
              <div className="space-y-5">
                <div>
                  <label htmlFor="s-fb" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Facebook (URL)</label>
                  <input id="s-fb" data-testid="settings-facebook" value={settings.facebook} onChange={setS("facebook")}
                    placeholder="https://facebook.com/suapagina" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="s-ig" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Instagram (URL)</label>
                  <input id="s-ig" data-testid="settings-instagram" value={settings.instagram} onChange={setS("instagram")}
                    placeholder="https://instagram.com/seuperfil" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="s-li" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">LinkedIn (URL)</label>
                  <input id="s-li" data-testid="settings-linkedin" value={settings.linkedin} onChange={setS("linkedin")}
                    placeholder="https://linkedin.com/company/suaempresa" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Texto do Banner Principal
              </h3>
              <div className="space-y-5">
                <div>
                  <label htmlFor="s-hero-title" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Título principal</label>
                  <input id="s-hero-title" data-testid="settings-hero-title" value={settings.hero_title} onChange={setS("hero_title")}
                    placeholder="O seu próximo automóvel começa aqui." className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="s-hero-subtitle" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Subtítulo</label>
                  <textarea id="s-hero-subtitle" data-testid="settings-hero-subtitle" rows={3} value={settings.hero_subtitle} onChange={setS("hero_subtitle")}
                    placeholder="Especialistas na comercialização e importação de viaturas da Europa…"
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <Bell className="w-4 h-4" /> Notificações de Pedidos
              </h3>
              <label htmlFor="s-notify" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                Email que recebe os pedidos de clientes
              </label>
              <input id="s-notify" data-testid="settings-notify-email" type="email" value={settings.notify_email || ""} onChange={setS("notify_email")}
                placeholder="oseuemail@exemplo.pt" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              <p className="mt-2 text-xs text-slate-400">Recebe um email sempre que um cliente envia um pedido de contacto, cotação de importação ou simulação de financiamento.</p>
            </div>

            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Mapa de Localização
              </h3>
              <label htmlFor="s-map" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">URL de incorporação do Google Maps</label>
              <input id="s-map" data-testid="settings-map" value={settings.map_embed_url || ""} onChange={setS("map_embed_url")}
                placeholder="https://www.google.com/maps/embed?pb=…" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              <p className="mt-2 text-xs text-slate-400">No Google Maps: Partilhar → Incorporar mapa → copie apenas o URL do src.</p>
            </div>

            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5 flex items-center gap-2">
                <Calculator className="w-4 h-4" /> Simulador de Financiamento
              </h3>
              <label htmlFor="s-rate" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Taxa de juro indicativa — TAN (%)</label>
              <input id="s-rate" data-testid="settings-interest-rate" type="number" min={0} max={30} step={0.1}
                value={settings.interest_rate ?? 7.5}
                onChange={(e) => setSettings((s) => ({ ...s, interest_rate: Number(e.target.value) }))}
                className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              <p className="mt-2 text-xs text-slate-400">Esta é a taxa usada no simulador público. Os clientes não a podem alterar — apenas visualizam. Atualize-a conforme as condições que pratica.</p>
            </div>

            <button type="submit" data-testid="settings-save-button"
              className="bg-[#0072C6] text-white font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#005FA3] transition-colors">
              Guardar Definições
            </button>
          </form>
        )}

        {/* CHANGE PASSWORD */}
        {/* CONTA (nome, email, password) */}
        {tab === "password" && (
          <div className="max-w-xl space-y-6">
            <form onSubmit={saveAccount} data-testid="admin-account-form" className="bg-white border border-slate-200 p-6 sm:p-8">
              <h2 className="font-display text-2xl text-[#0072C6] mb-6">Nome e Email de Acesso</h2>
              <div className="space-y-5">
                <div>
                  <label htmlFor="ac-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nome de utilizador</label>
                  <input id="ac-name" data-testid="ac-name" required minLength={2} value={account.name}
                    onChange={(e) => setAccount((a) => ({ ...a, name: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="ac-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Email de acesso</label>
                  <input id="ac-email" data-testid="ac-email" type="email" required value={account.email}
                    onChange={(e) => setAccount((a) => ({ ...a, email: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                  <p className="mt-1.5 text-xs text-slate-400">Se alterar o email, usará o novo para iniciar sessão.</p>
                </div>
              </div>
              <button type="submit" data-testid="ac-save" disabled={acSaving}
                className="mt-7 inline-flex items-center gap-2 bg-[#0072C6] text-white font-semibold px-8 py-4 text-sm hover:bg-[#005FA3] transition-colors disabled:opacity-60">
                {acSaving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar Nome / Email
              </button>
            </form>

            <form onSubmit={changePassword} data-testid="admin-password-form" className="bg-white border border-slate-200 p-6 sm:p-8">
              <h2 className="font-display text-2xl text-[#0072C6] mb-6">Alterar Palavra-passe</h2>
              <div className="space-y-5">
                <div>
                  <label htmlFor="pw-current" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Palavra-passe atual</label>
                  <input id="pw-current" data-testid="pw-current" type="password" required value={pw.current_password}
                    onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="pw-new" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nova palavra-passe (mín. 8 caracteres)</label>
                  <input id="pw-new" data-testid="pw-new" type="password" required minLength={8} value={pw.new_password}
                    onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label htmlFor="pw-confirm" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Confirmar nova palavra-passe</label>
                  <input id="pw-confirm" data-testid="pw-confirm" type="password" required minLength={8} value={pw.confirm}
                    onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
              </div>
              <button type="submit" data-testid="pw-save-button" disabled={pwSaving}
                className="mt-7 inline-flex items-center gap-2 bg-[#0072C6] text-white font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#005FA3] transition-colors disabled:opacity-60">
                {pwSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                <KeyRound className="w-4 h-4" /> Guardar Nova Palavra-passe
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Vehicle form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="vehicle-form-modal">
          <div className="bg-white w-full max-w-2xl my-8">
            <div className="flex items-center justify-between bg-[#0072C6] text-white px-6 py-4">
              <h3 className="font-display text-lg">{editing ? "Editar Viatura" : "Nova Viatura"}</h3>
              <button onClick={() => setShowForm(false)} aria-label="Fechar" data-testid="vehicle-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={saveVehicle} className="p-6 grid sm:grid-cols-2 gap-5" data-testid="vehicle-form">
              <div className="sm:col-span-2">
                <label htmlFor="v-title" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Título *</label>
                <input id="v-title" data-testid="v-title" required minLength={2} value={form.title} onChange={setF("title")}
                  placeholder="Ex.: BMW 320d Touring" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-category" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Categoria *</label>
                <select id="v-category" data-testid="v-category" value={form.category} onChange={setF("category")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="v-year" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Ano *</label>
                <input id="v-year" data-testid="v-year" type="number" required min={1950} max={2100} value={form.year} onChange={setF("year")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-km" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Quilómetros *</label>
                <input id="v-km" data-testid="v-km" type="number" required min={0} value={form.km} onChange={setF("km")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-price" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Preço (€) *</label>
                <input id="v-price" data-testid="v-price" type="number" required min={0} value={form.price} onChange={setF("price")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-fuel" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Combustível *</label>
                <select id="v-fuel" data-testid="v-fuel" value={form.fuel} onChange={setF("fuel")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
                  {["Gasolina", "Diesel", "Híbrido", "Elétrico", "GPL"].map((f) => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="v-trans" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Caixa *</label>
                <select id="v-trans" data-testid="v-trans" value={form.transmission} onChange={setF("transmission")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
                  {["Manual", "Automática"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="v-image" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Fotografias (galeria) *</label>
                <div className="flex flex-wrap items-center gap-3">
                  <input id="v-image" data-testid="v-image" type="text" value={form.image_url} onChange={setF("image_url")}
                    placeholder="URL da fotografia principal, ou carregue ficheiros" className="flex-1 min-w-[220px] border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                  <label data-testid="v-upload-button"
                    className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 text-sm font-medium px-5 py-3 cursor-pointer hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                    <Upload className="w-4 h-4" />
                    {uploading ? "A carregar…" : "Adicionar foto"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap gap-3" data-testid="v-images-preview">
                  {(form.images && form.images.length > 0 ? form.images : (form.image_url ? [form.image_url] : [])).map((src, i) => (
                    <div key={i} className="relative group">
                      <img src={src} alt="" className="w-24 h-16 object-cover border border-slate-200" />
                      <button type="button" onClick={() => removeImage(i)} aria-label="Remover"
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="v-video" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5">
                  <Film className="w-3.5 h-3.5" /> URL do vídeo (YouTube/Vimeo — opcional)
                </label>
                <input id="v-video" data-testid="v-video" type="url" value={form.video_url || ""} onChange={setF("video_url")}
                  placeholder="https://youtube.com/watch?v=…" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-badge" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Etiqueta (opcional)</label>
                <input id="v-badge" data-testid="v-badge" value={form.badge} onChange={setF("badge")}
                  placeholder="Ex.: Destaque, Oportunidade" className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div className="flex items-end pb-1 gap-6">
                <label className="inline-flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" data-testid="v-featured" checked={form.featured} onChange={setF("featured")}
                    className="w-4 h-4 accent-[#0072C6]" />
                  Destaque
                </label>
                <label className="inline-flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" data-testid="v-published" checked={form.published} onChange={setF("published")}
                    className="w-4 h-4 accent-[#0072C6]" />
                  Publicada no website
                </label>
              </div>

              {/* Campos do catálogo */}
              <div>
                <label htmlFor="v-brand" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Marca</label>
                <input id="v-brand" data-testid="v-brand" value={form.brand || ""} onChange={setF("brand")} placeholder="Ex.: BMW"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-model" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Modelo</label>
                <input id="v-model" data-testid="v-model" value={form.model || ""} onChange={setF("model")} placeholder="Ex.: 320d"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="v-version" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Versão</label>
                <input id="v-version" data-testid="v-version" value={form.version || ""} onChange={setF("version")} placeholder="Ex.: Touring M Sport"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-displacement" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Cilindrada (cm³)</label>
                <input id="v-displacement" data-testid="v-displacement" type="number" min={0} value={form.displacement ?? ""} onChange={setF("displacement")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-power" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Potência (cv)</label>
                <input id="v-power" data-testid="v-power" type="number" min={0} value={form.power ?? ""} onChange={setF("power")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-color" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Cor</label>
                <input id="v-color" data-testid="v-color" value={form.color || ""} onChange={setF("color")} placeholder="Ex.: Cinzento"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-doors" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Portas</label>
                <input id="v-doors" data-testid="v-doors" type="number" min={2} max={7} value={form.doors ?? ""} onChange={setF("doors")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label htmlFor="v-condition" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Estado</label>
                <select id="v-condition" data-testid="v-condition" value={form.condition} onChange={setF("condition")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
                  {["Novo", "Usado", "Seminovo"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="v-location" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Localização</label>
                <input id="v-location" data-testid="v-location" value={form.location || ""} onChange={setF("location")} placeholder="Ex.: Lisboa"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="v-description" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Descrição completa</label>
                <textarea id="v-description" data-testid="v-description" rows={4} value={form.description || ""} onChange={setF("description")}
                  placeholder="Descreva a viatura, histórico, estado, extras…"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="v-equipment" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Equipamento e extras (um por linha)</label>
                <textarea id="v-equipment" data-testid="v-equipment" rows={4} value={form.equipmentText || ""} onChange={setF("equipmentText")}
                  placeholder={"Ar condicionado\nCaixa automática\nCâmara traseira"}
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y font-mono" />
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" data-testid="vehicle-form-submit" disabled={saving}
                  className="inline-flex items-center gap-2 bg-[#0072C6] text-white font-semibold px-7 py-3.5 text-sm hover:bg-[#005FA3] transition-colors disabled:opacity-60">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editing ? "Guardar Alterações" : "Adicionar Viatura"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} data-testid="vehicle-form-cancel"
                  className="border border-slate-200 text-slate-600 font-semibold px-7 py-3.5 text-sm hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmDialog && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          data-testid="confirm-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeConfirm(); }}
        >
          <div className="bg-white w-full max-w-md shadow-xl" data-testid="confirm-modal">
            <div className="flex items-center justify-between bg-[#0B1E36] text-white px-6 py-4">
              <h3 className="font-display text-lg">{confirmDialog.title}</h3>
              <button onClick={closeConfirm} aria-label="Fechar" data-testid="confirm-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-700 leading-relaxed">{confirmDialog.message}</p>
              <div className="mt-7 flex justify-end gap-3">
                <button onClick={closeConfirm} data-testid="confirm-cancel"
                  className="border border-slate-200 text-slate-600 font-semibold px-5 py-2.5 text-sm hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                  Cancelar
                </button>
                <button onClick={async () => { const fn = confirmDialog.onConfirm; closeConfirm(); if (fn) await fn(); }} data-testid="confirm-ok"
                  className="bg-red-600 text-white font-semibold px-5 py-2.5 text-sm hover:bg-red-700 transition-colors">
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
